import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { chatApi, groupApi, getApiBaseUrl } from '../services/cloudApi';
import { getApiBaseUrlAsync } from '../config/apiConfig';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import { formatLastSeen } from '../utils/formatLastSeen';
import { socketManager, ConnectionState, disconnectSocket } from '../services/socketService';
import { playChatSound } from '../utils/chatSoundPresets';
import { showPlusGoldNotification, PlusLimits } from '../utils/plusGoldNotification';
import { isModalNotificationActive } from '../utils/adminNotificationSchedule';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { getLockActive } from '../utils/lockActiveStore';
import i18n from '../i18n';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { user, authToken, isAuthenticated, isPlusUser } = useAuth();
  const location = useLocation();
  const { preferences } = useSettings();
  const [friends, setFriends] = useState([]);
  const [messages, setMessages] = useState({}); // { friendId: [messages] }
  const [pendingAdminNotification, setPendingAdminNotification] = useState(null);
  const [selectedFriendId, setSelectedFriendId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false); // For red star indicator
  const [hasUnreadChatInBackground, setHasUnreadChatInBackground] = useState(false); // For ⭐ indicator when app was in background
  const [pendingFriendRequestCount, setPendingFriendRequestCount] = useState(0); // For friend request notification
  const [replyToMessage, setReplyToMessage] = useState(null); // { id, text, senderName } | null
  const [forwardMessage, setForwardMessage] = useState(null); // { id, text, senderName, type } | null
  /** When event:response is received, { messageId, groupId } so EventCardMessage can refetch participants */
  const [lastEventResponse, setLastEventResponse] = useState({ messageId: null, groupId: null });
  /** When poll:voted is received, { pollId, messageId?, groupId?, groupMessageId? } so PollCardMessage can refetch */
  const [lastPollVoted, setLastPollVoted] = useState({ pollId: null, messageId: null, groupId: null, groupMessageId: null });
  
  // Group chat state
  const [groups, setGroups] = useState([]);
  const [groupMessages, setGroupMessages] = useState({}); // { groupId: [messages] }
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupTyping, setGroupTyping] = useState({}); // { groupId: [{ userId, userName, avatarUrl }] }
  const [groupMessageReads, setGroupMessageReads] = useState({}); // { messageId: { readCount, totalMembers, allRead, readers: [userId] } }
  
  const socketRef = useRef(null);
  const typingTimeoutsRef = useRef({}); // { friendId: timeoutId }
  const groupTypingTimeoutsRef = useRef({}); // { groupId: timeoutId }
  const recentPinUpdatesRef = useRef({}); // { friendId: { pinned, pinnedAt, timestamp } } - Track recent pin updates to prevent race conditions
  
  // Debounce timers
  const loadUnreadCountTimerRef = useRef(null);
  const loadPendingFriendRequestCountTimerRef = useRef(null);
  const badgeUpdateTimerRef = useRef(null); // Badge güncelleme için debounce timer
  const lastBadgeCountRef = useRef(0); // Son gönderilen badge count
  const loadFriendsTimerRef = useRef(null);
  const selectFriendMarkAsReadTimeoutRef = useRef({}); // { friendId: timeoutId } - Track mark as read timeouts in selectFriend
  
  // Presence status management - track offline timeouts
  const offlineTimeoutsRef = useRef({}); // { friendId: timeoutId } - Track offline timeouts for 5 second delay
  const friendStatusCacheRef = useRef({}); // { friendId: { is_online, last_seen } } - Cache to prevent unnecessary updates
  
  // Track if socket has provided data (skip API calls if socket has data)
  const socketDataReceivedRef = useRef({
    unreadCount: false,
    friends: false,
    pendingRequests: false,
    groups: false,
  });
  
  // Group typing state tracking
  const groupTypingStateRef = useRef({}); // { groupId: Set<userId> }

  // Smart cache: loading state only when no cache (first load). Ref for synchronous read inside loadMessages.
  const [loadingMessagesForFriendId, setLoadingMessagesForFriendId] = useState(null);
  const messagesRef = useRef(messages);
  // AbortController ref — cancels in-flight DM fetch when friend changes
  const loadMessageAbortRef = useRef(null);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // SocketManager connection state for UI and re-attach on reconnect
  const [socketConnectionState, setSocketConnectionState] = useState(socketManager.getState());
  useEffect(() => {
    const unsub = socketManager.subscribe(setSocketConnectionState);
    return unsub;
  }, []);

  // Initialize Socket.IO and attach handlers (re-attach when socket instance changes after reconnect)
  useEffect(() => {
    if (!isAuthenticated || !authToken) {
      socketManager.disconnect();
      if (socketRef.current) {
        const handlers = socketRef.current._handlers;
        if (handlers) {
          const s = socketRef.current;
          s.off('user:status', handlers.handleUserStatus);
          s.off('message:new', handlers.handleMessageNew);
          s.off('message:delivered', handlers.handleMessageDelivered);
          s.off('message:read', handlers.handleMessageRead);
          s.off('message:read:ack', handlers.handleMessageReadAck);
          s.off('friends:updated', handlers.handleFriendsUpdated);
          s.off('unread-count:updated', handlers.handleUnreadCountUpdated);
          s.off('typing', handlers.handleTyping);
          s.off('user:profile:updated', handlers.handleUserProfileUpdated);
          s.off('user:avatar:updated', handlers.handleUserAvatarUpdated);
          s.off('friend:request', handlers.handleFriendRequest);
          s.off('friend:accepted', handlers.handleFriendAccepted);
          s.off('friend:cancelled', handlers.handleFriendCancelled);
          s.off('group:created', handlers.handleGroupCreated);
          s.off('group:updated', handlers.handleGroupUpdated);
          s.off('group:deleted', handlers.handleGroupDeleted);
          s.off('group:member:added', handlers.handleGroupMemberAdded);
          s.off('group:member:removed', handlers.handleGroupMemberRemoved);
          s.off('group:member:role_changed', handlers.handleGroupMemberRoleChanged);
          s.off('group:member:limit-reached', handlers.handleGroupMemberLimitReached);
          s.off('group:message:new', handlers.handleGroupMessageNew);
          s.off('group:message:read', handlers.handleGroupMessageRead);
          s.off('group:message:read:ack', handlers.handleGroupMessageReadAck);
          s.off('group:unread-count:updated', handlers.handleGroupUnreadCountUpdated);
          s.off('group:message:sent', handlers.handleGroupMessageSent);
          s.off('event:response', handlers.handleEventResponse);
          s.off('event:updated', handlers.handleEventUpdated);
          s.off('poll:voted', handlers.handlePollVoted);
          s.off('group:typing', handlers.handleGroupTyping);
          s.off('note:deleted', handlers.handleNoteDeleted);
          s.off('note:updated', handlers.handleNoteUpdated);
          s.off('note:collab-updated', handlers.handleNoteCollabUpdated);
          s.off('collab-invite:responded', handlers.handleCollabInviteResponded);
          s.off('group:system:message', handlers.handleGroupSystemMessage);
          s.off('group:locked', handlers.handleGroupLocked);
          s.off('group:unlocked', handlers.handleGroupUnlocked);
          s.off('group:destroyed', handlers.handleGroupDestroyed);
          s.off('group:left', handlers.handleGroupLeft);
          delete s._handlers;
        }
        socketRef.current = null;
      }
      return;
    }

    // When CONNECTED: attach handlers if we have a (possibly new) socket
    if (socketConnectionState === ConnectionState.CONNECTED) {
      const socket = socketManager.getSocket();
      if (socket && socket !== socketRef.current) {
        // Cleanup previous socket's handlers
        if (socketRef.current?._handlers) {
          const prev = socketRef.current;
          const h = prev._handlers;
          prev.off('user:status', h.handleUserStatus);
          prev.off('message:new', h.handleMessageNew);
          prev.off('message:delivered', h.handleMessageDelivered);
          prev.off('message:read', h.handleMessageRead);
          prev.off('message:read:ack', h.handleMessageReadAck);
          prev.off('friends:updated', h.handleFriendsUpdated);
          prev.off('unread-count:updated', h.handleUnreadCountUpdated);
          prev.off('typing', h.handleTyping);
          prev.off('user:profile:updated', h.handleUserProfileUpdated);
          prev.off('user:avatar:updated', h.handleUserAvatarUpdated);
          prev.off('friend:request', h.handleFriendRequest);
          prev.off('friend:accepted', h.handleFriendAccepted);
          prev.off('friend:cancelled', h.handleFriendCancelled);
          prev.off('group:created', h.handleGroupCreated);
          prev.off('group:updated', h.handleGroupUpdated);
          prev.off('group:deleted', h.handleGroupDeleted);
          prev.off('group:member:added', h.handleGroupMemberAdded);
          prev.off('group:member:removed', h.handleGroupMemberRemoved);
          prev.off('group:member:role_changed', h.handleGroupMemberRoleChanged);
          prev.off('group:member:limit-reached', h.handleGroupMemberLimitReached);
          prev.off('group:message:new', h.handleGroupMessageNew);
          prev.off('group:message:read', h.handleGroupMessageRead);
          prev.off('group:message:read:ack', h.handleGroupMessageReadAck);
          prev.off('group:unread-count:updated', h.handleGroupUnreadCountUpdated);
          prev.off('group:message:sent', h.handleGroupMessageSent);
          prev.off('event:response', h.handleEventResponse);
          prev.off('event:updated', h.handleEventUpdated);
          prev.off('poll:voted', h.handlePollVoted);
          prev.off('group:typing', h.handleGroupTyping);
          prev.off('note:deleted', h.handleNoteDeleted);
          prev.off('note:updated', h.handleNoteUpdated);
          prev.off('note:collab-updated', h.handleNoteCollabUpdated);
          prev.off('collab-invite:responded', h.handleCollabInviteResponded);
          prev.off('group:system:message', h.handleGroupSystemMessage);
          prev.off('group:locked', h.handleGroupLocked);
          prev.off('group:unlocked', h.handleGroupUnlocked);
          prev.off('group:destroyed', h.handleGroupDestroyed);
          prev.off('group:left', h.handleGroupLeft);
          delete prev._handlers;
        }
        socketRef.current = socket;

      // CRITICAL: Use named handler functions so they can be safely removed
      // Handle user:status event (friend online/offline)
      const handleUserStatus = (data) => {
        const { user_id, user_email, is_online, last_seen } = data;
        
        const friendId = user_email;
        const cachedStatus = friendStatusCacheRef.current[friendId];
        
        // Check if status actually changed
        if (cachedStatus && 
            cachedStatus.is_online === is_online && 
            cachedStatus.last_seen === last_seen) {
          // Status unchanged, skip update
          return;
        }
        
        // Update cache
        friendStatusCacheRef.current[friendId] = { is_online, last_seen };
        
        // If going offline, add 5 second delay
        if (is_online === false && cachedStatus?.is_online === true) {
          // Clear any existing timeout
          if (offlineTimeoutsRef.current[friendId]) {
            clearTimeout(offlineTimeoutsRef.current[friendId]);
          }
          
          // Set 5 second delay before updating to offline
          offlineTimeoutsRef.current[friendId] = setTimeout(() => {
            // Check if user came back online during delay
            const currentCache = friendStatusCacheRef.current[friendId];
            if (currentCache?.is_online === true) {
              // User came back online, cancel offline update
              delete offlineTimeoutsRef.current[friendId];
              return;
            }
            
            // Update to offline
            setFriends((prev) =>
              prev.map((friend) => {
                const fId = friend.email || friend.id;
                if (fId === friendId) {
                  let statusText = '';
                  let statusDot = 'gray';
                  
                  if (last_seen) {
                    const formatted = formatLastSeen(last_seen);
                    statusText = formatted || '';
                  }
                  
                  return {
                    ...friend,
                    is_online: false,
                    last_seen,
                    status: 'offline',
                    statusText,
                    statusDot,
                  };
                }
                return friend;
              })
            );
            
            delete offlineTimeoutsRef.current[friendId];
          }, 5000);
          
          return; // Don't update immediately
        }
        
        // If going online or status changed (but not offline transition), update immediately
        // Also clear any pending offline timeout
        if (offlineTimeoutsRef.current[friendId]) {
          clearTimeout(offlineTimeoutsRef.current[friendId]);
          delete offlineTimeoutsRef.current[friendId];
        }
        
        setFriends((prev) =>
          prev.map((friend) => {
            const fId = friend.email || friend.id;
            // Match by email
            if (fId === friendId) {
              let statusText = i18n.t('chat:chatContext.onlineStatus');
              let statusDot = 'green';

              if (is_online === true) {
                statusText = i18n.t('chat:chatContext.onlineStatus');
                statusDot = 'green';
              } else {
                if (last_seen) {
                  const formatted = formatLastSeen(last_seen);
                  statusText = formatted || '';
                } else {
                  statusText = '';
                }
                statusDot = 'gray';
              }
              
              return {
                ...friend,
                is_online,
                last_seen,
                status: is_online === true ? 'online' : 'offline',
                statusText,
                statusDot,
              };
            }
            return friend;
          })
        );
      };
      
      socket.on('user:status', handleUserStatus);

      // Handle message:new event (realtime message received)
      const handleMessageNew = async (data) => {
        const { message } = data;
        if (!message) return;

        // Development-only logging
        if (process.env.NODE_ENV === 'development') {
          console.log('📨 [Socket Client] message:new received:', message.id);
        }

        // CRITICAL: Ignore messages that we sent ourselves
        // If sender_id matches current user, this is our own message (already handled by message:sent ACK)
        if (message.sender_id === user?.id) {
          // Development-only logging
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ [Socket Client] Ignoring own message from message:new event:', message.id);
          }
          return;
        }

        // CRITICAL: DO NOT update sender's online status when receiving message
        // Message receiving does NOT mean sender is online - they might be offline
        // Online status should ONLY be set via explicit 'user:online' event (when app is active)
        // Socket connection alone does NOT mean user is online - app must be active and visible

        // Use sender_email from message (server provides it)
        const friendId = message.sender_email || message.sender_id;

        // Resolve file URL if needed
        let fileUrl = message.file_url;
        if (fileUrl && !fileUrl.startsWith('http')) {
          try {
            const apiBaseUrl = await getApiBaseUrlAsync();
            const baseUrl = apiBaseUrl.replace('/api', '');
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          } catch (error) {
            console.warn('Failed to resolve file URL:', error);
          }
        }

        // Add message to state
        let messageAlreadyExists = false;
        setMessages((prev) => {
          const friendMessages = prev[friendId] || [];
          
          // DEBUG: Log note_share message
          if (message.type === 'note_share') {
            console.log('[ChatContext] Received note_share message:', {
              message,
              note_title: message.note_title,
              note_content: message.note_content,
              note_title_color: message.note_title_color,
            });
          }
          // Check if message already exists (by ID or by content+timestamp for duplicate prevention)
          const exists = friendMessages.some((m) => 
            m.id === message.id || 
            (m.id?.startsWith('temp_') && m.content === message.content && Math.abs(new Date(m.timestamp) - new Date(message.created_at)) < 5000)
          );
          if (exists) {
            // Development-only logging
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ [Socket Client] Message already exists, skipping:', message.id);
            }
            messageAlreadyExists = true;
            return prev;
          }

          return {
            ...prev,
            [friendId]: [...friendMessages, {
              id: message.id,
              senderId: 'other_user',
              receiverId: message.receiver_id,
              senderEmail: message.sender_email || null,
              senderName: message.sender_name || null,
              senderIsPlus: message.sender_isPlus || false, // Add Plus membership status from backend
              content: message.content,
              type: message.type || 'text',
              file_url: fileUrl,
              file_name: message.file_name,
              // Note share fields
              note_id: message.note_id || null,
              note_title: message.note_title || null,
              note_content: message.note_content || null,
              note_title_color: message.note_title_color || null,
              // Event share fields
              event_id: message.event_id || null,
              event_title: message.event_title || null,
              event_start: message.event_start || null,
              event_end: message.event_end || null,
              event_all_day: message.event_all_day || null,
              event_description: message.event_description || null,
              event_location: message.event_location || null,
              // Poll and contact
              poll_id: message.poll_id || null,
              poll_title: message.poll_title || null,
              poll_multiple_choice: message.poll_multiple_choice || null,
              contact_user_id: message.contact_user_id || null,
              contact_name: message.contact_name || null,
              contact_avatar_url: message.contact_avatar_url || null,
              contact_email: message.contact_email || null,
              timestamp: message.created_at,
              isRead: message.is_read,
              delivered_at: message.delivered_at || null,
              read_at: message.read_at || null,
              replyTo: message.replyTo || null,
              isForwarded: message.isForwarded || false,
              forwardedFromMessageId: message.forwardedFromMessageId || null,
              forwardedFromSenderId: message.forwardedFromSenderId || null,
            }],
          };
        });

        if (!messageAlreadyExists) {
          setFriends((prev) =>
            prev.map((friend) => {
              const fId = friend.email || friend.id;
              if (fId === friendId) {
                return {
                  ...friend,
                  lastMessage: message.content || message.file_name || '',
                  lastMessageTime: message.created_at,
                  unreadCount: (friend.unreadCount || 0) + 1,
                };
              }
              return friend;
            })
          );
        }

        // Show Windows notification if app is minimized or in tray
        if (!messageAlreadyExists && typeof window !== 'undefined' && window.api?.isAppMinimized) {
          try {
            const isMinimized = await window.api.isAppMinimized();
            if (isMinimized) {
              const locked = getLockActive(user?.id);
              const friend = friends.find(f => (f.email || f.id) === friendId);
              const friendName = friend?.nickname || friend?.name || friend?.email?.split('@')[0] || i18n.t('chat:chatContext.someoneFallback');

              await window.api.showNotification({
                title: locked
                  ? i18n.t('lock:notifications.newMessageTitle')
                  : friendName,
                body: locked
                  ? i18n.t('lock:notifications.newMessageBody')
                  : message.type === 'text'
                    ? (message.content?.substring(0, 100) || i18n.t('chat:chatContext.newMessageFallback'))
                    : message.type === 'image'
                      ? i18n.t('chat:chatContext.photoSentPreview')
                      : message.type === 'file'
                        ? i18n.t('chat:chatContext.fileSentPreview', { fileName: message.file_name || i18n.t('chat:chatContext.fileFallback') })
                        : i18n.t('chat:chatContext.newMessageFallback'),
                sound: !preferences?.chatNotificationsMuted,
                meta: {
                  type: 'chat',
                  friendId: friendId,
                },
              });
            }
          } catch (error) {
            console.warn('Windows notification gösterilirken hata:', error);
          }
        }

        // CRITICAL: Mark as delivered immediately when message is received
        // This triggers the delivered_at update on server
        // NOTE: This does NOT mean receiver is online - message can be delivered even if app is closed
        // (Socket might be connected but app might be minimized/closed)
        if (message.receiver_id === user?.id && socketRef.current && socketRef.current.connected) {
          // Development-only logging
          if (process.env.NODE_ENV === 'development') {
            console.log('✓✓ [Socket Client] Emitting message:delivered for:', message.id);
          }
          socketRef.current.emit('message:delivered', { message_id: message.id });
        }

        // Show notification using policy-based system
        // CRITICAL: Bildirim mantığı - Sadece ve sadece hem o kişi seçiliyse hem de sohbet sayfasındaysak bildirimi engelle
        // Diğer tüm durumlarda (başka sekmedeysek, mesaj zaten varsa bile) bildirim fonksiyonu çalışmalı
        if (handleChatNotificationRef.current) {
          // Emergency Sound Check: Hem locationRef hem de window.location.pathname kontrolü
          const locationRefPath = locationRef.current;
          const windowLocationPath = typeof window !== 'undefined' && window.location ? 
            (window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname) : 
            null;
          const currentPath = windowLocationPath || locationRefPath;
          
          const isUserOnChatPage = currentPath === '/chat' || currentPath.startsWith('/chat');
          const isMessageFromSelectedFriend = selectedFriendIdRef.current === friendId;
          
          // Sadece ve sadece hem o kişi seçiliyse hem de sohbet sayfasındaysak bildirimi engelle
          // Diğer tüm durumlarda (başka sekmedeysek, mesaj zaten varsa bile) bildirim fonksiyonu çalışmalı
          const shouldSkipNotification = isUserOnChatPage && isMessageFromSelectedFriend;
          
          if (!shouldSkipNotification) {
            // Bildirim göster: Kullanıcı başka sekmedeyse VEYA mesaj farklı birinden geldiyse
            handleChatNotificationRef.current(friendId, {
              id: message.id,
              content: message.content || message.file_name || '',
            });
          } else {
            // Development-only logging
            if (process.env.NODE_ENV === 'development') {
              console.log('🔕 [Socket Client] Notification skipped: User is on chat page and message is from selected friend');
            }
          }
        }

        // Reload unread count (skip debounce for real-time updates)
        // But don't reload if socket is connected (socket handles this)
        if (!socketRef.current?.connected) {
          loadUnreadCount(true);
        } else {
          // Socket is connected, update unread count from socket data
          // The socket message:new event will update unreadCount via friends state
          socketDataReceivedRef.current.unreadCount = true;
        }
      };
      
      socket.on('message:new', handleMessageNew);

      // Handle message:delivered event
      const handleMessageDelivered = (data) => {
        const { message_id, delivered_at } = data;
        // Development-only logging
        if (process.env.NODE_ENV === 'development') {
          console.log('✓✓ [Socket Client] message:delivered received:', message_id);
        }
        
        setMessages((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((friendId) => {
            updated[friendId] = updated[friendId].map((msg) =>
              msg.id === message_id
                ? { ...msg, delivered_at }
                : msg
            );
          });
          return updated;
        });
      };
      
      socket.on('message:delivered', handleMessageDelivered);

      // Handle message:read event
      const handleMessageRead = (data) => {
        const { message_id, read_at } = data;
        // Development-only logging
        if (process.env.NODE_ENV === 'development') {
          console.log('✓✓ [Socket Client] message:read received (blue tick):', message_id, 'read_at:', read_at);
        }
        
        let messageFriendId = null;
        setMessages((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((friendId) => {
            const messageIndex = updated[friendId].findIndex((msg) => msg.id === message_id);
            if (messageIndex !== -1) {
              messageFriendId = friendId;
              // Only update if message is from current user (own messages)
              const message = updated[friendId][messageIndex];
              if (message.senderId === 'current_user') {
                updated[friendId] = updated[friendId].map((msg) =>
                  msg.id === message_id
                    ? { ...msg, read_at: read_at || new Date().toISOString(), isRead: true }
                    : msg
                );
                // Development-only logging
                if (process.env.NODE_ENV === 'development') {
                  console.log('✓✓ [Socket Client] Updated message read_at:', message_id, 'read_at:', read_at || new Date().toISOString());
                }
              }
            }
          });
          return updated;
        });

        // Update friend's unreadCount if message was found
        if (messageFriendId) {
          setFriends((prev) => {
            const updated = prev.map((friend) => {
              const friendId = friend.email || friend.id;
              if (friendId === messageFriendId && friend.unreadCount > 0) {
                return { ...friend, unreadCount: Math.max(0, friend.unreadCount - 1) };
              }
              return friend;
            });
            
            // Update global unread count from friends state (socket provided data)
            socketDataReceivedRef.current.unreadCount = true;
            const totalUnread = updated.reduce((sum, f) => sum + (f.unreadCount || 0), 0);
            setUnreadCount(totalUnread);
            
            return updated;
          });
        }
      };
      
      // Handle message:read:ack event (confirmation that we marked a message as read)
      const handleMessageReadAck = (data) => {
        const { message_id, read_at } = data;
        
        // Update local state - mark the message we read as read
        setMessages((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((friendId) => {
            const messageIndex = updated[friendId].findIndex((msg) => msg.id === message_id);
            if (messageIndex !== -1) {
              const message = updated[friendId][messageIndex];
              // Update if message is NOT from current user (messages we received and read)
              if (message.senderId !== 'current_user') {
                updated[friendId] = updated[friendId].map((msg) =>
                  msg.id === message_id
                    ? { ...msg, isRead: true, read_at: read_at || new Date().toISOString() }
                    : msg
                );
              }
            }
          });
          return updated;
        });
      };
      
      socket.on('message:read', handleMessageRead);
      socket.on('message:read:ack', handleMessageReadAck);

      const handleEventResponse = (data) => {
        const { messageId, groupId } = data;
        setLastEventResponse({ messageId: messageId || null, groupId: groupId || null });
      };
      socket.on('event:response', handleEventResponse);

      const handleEventUpdated = (data) => {
        const { messageId, groupId, message: updatedMessage } = data;
        if (!messageId || !updatedMessage || updatedMessage.type !== 'event_cancelled') return;
        if (groupId) {
          setGroupMessages((prev) => {
            const list = prev[groupId];
            if (!list) return prev;
            const next = list.map((m) => {
              const id = m.id ?? m.message_id;
              if (String(id) !== String(messageId)) return m;
              return { ...m, type: 'event_cancelled', content: updatedMessage.content || i18n.t('chat:chatContext.eventCancelledFallback') };
            });
            return { ...prev, [groupId]: next };
          });
        } else {
          setMessages((prev) => {
            let changed = false;
            const next = {};
            for (const [friendId, list] of Object.entries(prev)) {
              const arr = (list || []).map((m) => {
                const id = m.id ?? m.message_id;
                if (String(id) !== String(messageId)) return m;
                changed = true;
                return { ...m, type: 'event_cancelled', content: updatedMessage.content || i18n.t('chat:chatContext.eventCancelledFallback') };
              });
              next[friendId] = arr;
            }
            return changed ? next : prev;
          });
        }
      };
      socket.on('event:updated', handleEventUpdated);

      const handlePollVoted = (data) => {
        const { pollId, messageId, groupId, groupMessageId } = data;
        setLastPollVoted({ pollId: pollId || null, messageId: messageId || null, groupId: groupId || null, groupMessageId: groupMessageId || null });
      };
      socket.on('poll:voted', handlePollVoted);
      
      // Handle friends list update from socket (if socket provides this event)
      const handleFriendsUpdated = () => {
        socketDataReceivedRef.current.friends = true;
      };
      
      socket.on('friends:updated', handleFriendsUpdated);
      
      // Handle unread count update from socket (if socket provides this event)
      const handleUnreadCountUpdated = (data) => {
        if (data?.count !== undefined) {
          socketDataReceivedRef.current.unreadCount = true;
          setUnreadCount(data.count);
          const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
          setHasUnreadMessages(!isOnChatPage && data.count > 0);
        }
      };
      
      socket.on('unread-count:updated', handleUnreadCountUpdated);
      
      // Mark that socket is connected and can provide data
      socketDataReceivedRef.current.friends = true;
      socketDataReceivedRef.current.unreadCount = true;

      // Handle typing event
      const handleTyping = (data) => {
        const { from, from_email, isTyping } = data;
        // This will be handled by ChatPage component
        // Use email to identify friend
        window.dispatchEvent(new CustomEvent('notesplus-typing', {
          detail: { from: from_email || from, isTyping },
        }));
      };
      
      socket.on('typing', handleTyping);

      // Handle user:profile:updated event (backward compat — name/avatar changes)
      const handleUserProfileUpdated = (data) => {
        const { user_id, name, avatarUrl } = data;
        if (!user_id) return;
        const uid = String(user_id);
        // Inline update — avoid full reload for base64-free URL payloads
        if (avatarUrl && !avatarUrl.startsWith('data:')) {
          setFriends(prev => prev.map(f => String(f.id) === uid ? { ...f, avatarUrl, avatar_url: avatarUrl } : f));
          setGroups(prev => prev.map(g => ({
            ...g,
            members: (g.members || []).map(m => String(m.id) === uid ? { ...m, avatarUrl, avatar_url: avatarUrl } : m),
          })));
        } else {
          loadFriends(true);
        }
        if (name) {
          setFriends(prev => prev.map(f => String(f.id) === uid ? { ...f, name } : f));
        }
      };

      socket.on('user:profile:updated', handleUserProfileUpdated);

      // Handle user:avatar:updated — dedicated avatar-only update (URL, never base64)
      const handleUserAvatarUpdated = ({ userId, avatarUrl }) => {
        if (!userId) return;
        const uid = String(userId);
        setFriends(prev => prev.map(f => String(f.id) === uid ? { ...f, avatarUrl, avatar_url: avatarUrl } : f));
        setGroups(prev => prev.map(g => ({
          ...g,
          members: (g.members || []).map(m => String(m.id) === uid ? { ...m, avatarUrl, avatar_url: avatarUrl } : m),
        })));
      };

      socket.on('user:avatar:updated', handleUserAvatarUpdated);

      // Handle friend:request event - Show toast notification when someone adds you as friend
      const handleFriendRequest = (data) => {
        const { sender_email, sender_name, message } = data;
        console.log('📨 [Socket Client] friend:request received:', { sender_email, sender_name, message });
        
        // Show toast notification
        if (typeof window !== 'undefined') {
          const toastEvent = new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'info',
              title: i18n.t('chat:chatContext.friendRequestTitle'),
              message: message || i18n.t('chat:chatContext.friendRequestMessage', { name: sender_email || i18n.t('chat:chatContext.someoneFallback') }),
            },
          });
          
          console.log('🔔 [Socket Client] Dispatching toast event:', toastEvent.detail);
          window.dispatchEvent(toastEvent);
        } else {
          console.warn('⚠️ [Socket Client] window is undefined, cannot dispatch toast event');
        }
        
        // Reload incoming friend requests (realtime sync)
        loadPendingFriendRequestCount(true);
      };
      
      socket.on('friend:request', handleFriendRequest);

      // Handle friend:accepted event - When someone accepts your friend request or you accept theirs
      const handleFriendAccepted = (data) => {
        const { friend_id, friend_email, friend_name, friend_avatar_url, message, request_id } = data;
        console.log('✅ [Socket Client] friend:accepted received:', { friend_id, friend_email, friend_name, message, request_id });
        
        // Show toast notification
        if (typeof window !== 'undefined') {
          const toastEvent = new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: i18n.t('chat:chatContext.friendRequestAcceptedTitle'),
              message: message || i18n.t('chat:chatContext.friendRequestAcceptedMessage', { name: friend_email || i18n.t('chat:chatContext.someoneFallback') }),
            },
          });
          
          window.dispatchEvent(toastEvent);
        }
        
        // CRITICAL: Real-time state update - Optimistically add friend to state immediately
        // This ensures UI updates instantly without waiting for API response
        if (friend_email) {
          setFriends((prevFriends) => {
            // Check if friend already exists in state
            const friendExists = prevFriends.some(f => (f.email || f.id) === friend_email || f.id === friend_id);
            if (friendExists) {
              // Friend already exists - update existing friend with latest data
              console.log('🔄 [Sync] Friend already in state, updating with latest data...');
              return prevFriends.map((friend) => {
                const fId = friend.email || friend.id;
                if (fId === friend_email || friend.id === friend_id) {
                  // Update existing friend with latest data from socket event
                  return {
                    ...friend,
                    name: friend_name || friend.name || null,
                    avatarUrl: friend_avatar_url || friend.avatarUrl || null,
                    id: friend_id || friend.id || friend_email,
                    // Preserve other properties
                  };
                }
                return friend;
              });
            }
            
            // Add new friend optimistically
            const newFriend = {
              email: friend_email,
              name: friend_name || null,
              avatarUrl: friend_avatar_url || null,
              id: friend_id || friend_email, // Use friend_id if available, fallback to email
              status: 'offline',
              statusText: '',
              statusDot: 'gray',
              pinned: false,
              unreadCount: 0,
              lastMessage: null,
              lastMessageTime: null,
            };
            
            console.log('✅ [Sync] Optimistically added friend to state:', friend_email);
            return [...prevFriends, newFriend];
          });
        }
        
        // CRITICAL: Notify FriendsListModal to remove accepted request from outgoing requests
        // This ensures outgoing requests list updates immediately without page refresh
        if (typeof window !== 'undefined' && request_id) {
          window.dispatchEvent(new CustomEvent('notesplus-friend-request-accepted', {
            detail: { request_id, friend_email, friend_id }
          }));
          console.log('🔄 [Sync] Dispatched friend-request-accepted event for request_id:', request_id);
        }
        
        // CRITICAL: Full reload of friends list and requests (realtime sync)
        // This ensures we get the latest data from backend (including role, isPlus, etc.)
        // CRITICAL FIX: Reset socket data flag to force API reload
        // This ensures we get fresh data from backend even if socket already provided data
        socketDataReceivedRef.current.friends = false;
        loadFriends(true); // Skip debounce for realtime updates, force API reload
        loadPendingFriendRequestCount(true);
      };
      
      socket.on('friend:accepted', handleFriendAccepted);

      // Handle friend:cancelled event - When someone cancels their friend request to you
      const handleFriendCancelled = (data) => {
        const { sender_email, message } = data;
        console.log('❌ [Socket Client] friend:cancelled received:', { sender_email, message });
        
        // Show toast notification
        if (typeof window !== 'undefined') {
          const toastEvent = new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'info',
              title: i18n.t('chat:chatContext.friendRequestCancelledTitle'),
              message: message || i18n.t('chat:chatContext.friendRequestCancelledMessage', { name: sender_email || i18n.t('chat:chatContext.someoneFallback') }),
            },
          });
          
          window.dispatchEvent(toastEvent);
        }
        
        // Reload incoming friend requests (realtime sync)
        loadPendingFriendRequestCount(true);
      };
      
      socket.on('friend:cancelled', handleFriendCancelled);

      // ============================================
      // GROUP CHAT SOCKET EVENTS
      // ============================================

      // Handle group:created event
      const handleGroupCreated = (data) => {
        const { group } = data;
        console.log('✅ [Socket Client] group:created received:', group.id);
        
        // Reload groups (realtime sync)
        loadGroups(true);
      };
      
      socket.on('group:created', handleGroupCreated);

      // Handle group:updated event
      const handleGroupUpdated = (data) => {
        const { group } = data;
        console.log('🔄 [Socket Client] group:updated received:', group.id);
        
        // Update group in state
        setGroups((prev) =>
          prev.map((g) => (g.id === group.id ? { ...g, ...group } : g))
        );
      };
      
      socket.on('group:updated', handleGroupUpdated);

      // Handle group:deleted event
      const handleGroupDeleted = (data) => {
        const { groupId } = data;
        console.log('❌ [Socket Client] group:deleted received:', groupId);
        
        // Remove group from state
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        setGroupMessages((prev) => {
          const updated = { ...prev };
          delete updated[groupId];
          return updated;
        });
        
        // If this group was selected, deselect it
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
      };
      
      socket.on('group:deleted', handleGroupDeleted);

      // Handle group:member:added event
      const handleGroupMemberAdded = (data) => {
        const { groupId, group, member } = data;
        console.log('➕ [Socket Client] group:member:added received:', { groupId, memberEmail: member.email });

        // Reload groups (realtime sync)
        loadGroups(true);
        // Notify GroupChatView to reload member list so new member's messages show correctly
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-group-role-changed', {
            detail: { groupId, memberId: member?.id, newRole: member?.role },
          }));
        }
      };
      
      socket.on('group:member:added', handleGroupMemberAdded);

      // Handle group:member:removed event
      const handleGroupMemberRemoved = (data) => {
        const { groupId, memberId } = data;
        console.log('➖ [Socket Client] group:member:removed received:', { groupId, memberId });
        
        // If current user was removed, clear group data
        if (memberId === user?.id) {
          setGroups((prev) => prev.filter((g) => g.id !== groupId));
          setGroupMessages((prev) => {
            const updated = { ...prev };
            delete updated[groupId];
            return updated;
          });
          if (selectedGroupId === groupId) {
            setSelectedGroupId(null);
          }
        } else {
          // Reload groups (realtime sync)
          loadGroups(true);
        }
      };
      
      socket.on('group:member:removed', handleGroupMemberRemoved);

      // Handle group:member:role_changed event (promote/demote)
      const handleGroupMemberRoleChanged = (data) => {
        const { groupId, memberId, newRole } = data;
        // Reload groups so member list reflects the new role
        loadGroups(true);
        // Dispatch window event so GroupChatView can re-fetch members
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-group-role-changed', {
            detail: { groupId, memberId, newRole },
          }));
        }
      };

      socket.on('group:member:role_changed', handleGroupMemberRoleChanged);

      // Handle group:member:limit-reached event
      const handleGroupMemberLimitReached = (data) => {
        const { message } = data;
        console.log('⚠️ [Socket Client] group:member:limit-reached received:', message);
        
        // Show toast notification
        if (typeof window !== 'undefined') {
          const toastEvent = new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'warning',
              title: i18n.t('chat:chatContext.groupLimitToastTitle'),
              message: message || i18n.t('chat:chatContext.groupLimitReachedFallback'),
            },
          });
          
          window.dispatchEvent(toastEvent);
        }
      };
      
      socket.on('group:member:limit-reached', handleGroupMemberLimitReached);

      // Handle group:message:new event
      const handleGroupMessageNew = async (data) => {
        const { groupId, message } = data;
        if (!message) return;

        // Development-only logging
        if (process.env.NODE_ENV === 'development') {
          console.log('📨 [Socket Client] group:message:new received:', message.id, 'in group', groupId);
        }

        // CRITICAL: Ignore messages that we sent ourselves
        if (message.sender_id === user?.id) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ [Socket Client] Ignoring own group message from group:message:new event:', message.id);
          }
          return;
        }

        // Resolve file URL if needed
        let fileUrl = message.file_url;
        if (fileUrl && !fileUrl.startsWith('http')) {
          try {
            const apiBaseUrl = await getApiBaseUrlAsync();
            const baseUrl = apiBaseUrl.replace('/api', '');
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          } catch (error) {
            console.warn('Failed to resolve file URL:', error);
          }
        }

        // Add message to state
        let messageAlreadyExists = false;
        setGroupMessages((prev) => {
          const groupMsgs = prev[groupId] || [];
          const exists = groupMsgs.some((m) => 
            m.id === message.id || 
            (m.id?.startsWith('temp_') && m.content === message.content && Math.abs(new Date(m.timestamp) - new Date(message.created_at)) < 5000)
          );
          if (exists) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ [Socket Client] Group message already exists, skipping:', message.id);
            }
            messageAlreadyExists = true;
            return prev;
          }

          // DEBUG: Log note_share group message
          if (message.type === 'note_share') {
            console.log('[ChatContext] Received note_share group message:', {
              message,
              note_title: message.note_title,
              note_content: message.note_content,
              note_title_color: message.note_title_color,
            });
          }

          return {
            ...prev,
            [groupId]: [...groupMsgs, {
              id: message.id,
              senderId: message.sender_id,
              senderEmail: message.sender_email,
              senderName: message.sender_name || null,
              senderAvatarUrl: message.sender_avatar_url || null,
              senderIsPlus: message.sender_isPlus || false,
              content: message.content,
              type: message.type || 'text',
              systemEvent: message.system_event || null,
              systemParams: message.system_params || null,
              file_url: fileUrl,
              fileUrl,
              file_name: message.file_name || message.fileName,
              fileName: message.file_name || message.fileName,
              // Note share fields
              note_id: message.note_id || null,
              note_title: message.note_title || null,
              note_content: message.note_content || null,
              note_title_color: message.note_title_color || null,
              // Event share fields
              event_id: message.event_id || null,
              event_title: message.event_title || null,
              event_start: message.event_start || null,
              event_end: message.event_end || null,
              event_all_day: message.event_all_day || null,
              event_description: message.event_description || null,
              event_location: message.event_location || null,
              // Poll and contact
              poll_id: message.poll_id || null,
              poll_title: message.poll_title || null,
              poll_multiple_choice: message.poll_multiple_choice || null,
              contact_user_id: message.contact_user_id || null,
              contact_name: message.contact_name || null,
              contact_avatar_url: message.contact_avatar_url || null,
              contact_email: message.contact_email || null,
              timestamp: message.created_at,
              replyTo: message.replyTo || null,
              isForwarded: message.isForwarded || false,
              forwardedFromMessageId: message.forwardedFromMessageId || null,
              forwardedFromSenderId: message.forwardedFromSenderId || null,
              mentionedUserIds: message.mentionedUserIds || [],
            }],
          };
        });

        // Show Windows notification if app is minimized or in tray (for group messages)
        if (!messageAlreadyExists && message.type !== 'system' && typeof window !== 'undefined' && window.api?.isAppMinimized) {
          try {
            const isMinimized = await window.api.isAppMinimized();
            if (isMinimized) {
              const locked = getLockActive(user?.id);
              const group = groups.find(g => g.id === groupId);
              const groupName = group?.name || i18n.t('chat:chatContext.groupFallback');
              const senderName = message.sender_name || message.sender_email?.split('@')[0] || i18n.t('chat:chatContext.someoneFallback');

              await window.api.showNotification({
                title: locked
                  ? i18n.t('lock:notifications.newMessageTitle')
                  : `${groupName}`,
                body: locked
                  ? i18n.t('lock:notifications.newMessageBody')
                  : `${senderName}: ${message.type === 'text'
                    ? (message.content?.substring(0, 100) || i18n.t('chat:chatContext.newMessageFallback'))
                    : message.type === 'image'
                      ? i18n.t('chat:chatContext.photoSentPreview')
                      : message.type === 'file'
                        ? i18n.t('chat:chatContext.fileSentPreview', { fileName: message.file_name || i18n.t('chat:chatContext.fileFallback') })
                        : i18n.t('chat:chatContext.newMessageFallback')}`,
                sound: !preferences?.groupNotificationsMuted,
                meta: {
                  type: 'chat',
                  groupId: groupId,
                },
              });
            }
          } catch (error) {
            console.warn('Windows notification gösterilirken hata:', error);
          }
        }

        // Update group's last message (only if message was actually added and not a system message)
        if (!messageAlreadyExists && message.type !== 'system') {
          setGroups((prev) => {
            const existingGroup = prev.find((g) => g.id === groupId);
            
            // If group doesn't exist in state (was hidden), reload groups to get it back
            if (!existingGroup) {
              // Group was hidden but new message arrived - reload groups to show it again
              loadGroups(true);
              return prev;
            }
            
            // Update existing group
            return prev.map((group) => {
              if (group.id === groupId) {
                return {
                  ...group,
                  lastMessage: message.content || message.file_name || '',
                  lastMessageTime: message.created_at,
                  unreadCount: (group.unreadCount || 0) + 1,
                };
              }
              return group;
            });
          });
        }

        // Mark as read if group is selected and user is on chat page
        const isGroupSelected = selectedGroupIdRef.current === groupId;
        const isOnChatPage = locationRef.current === '/chat' || locationRef.current.startsWith('/chat');
        
        if (isGroupSelected && isOnChatPage && socketRef.current && socketRef.current.connected) {
          // Auto-mark as read
          socketRef.current.emit('group:message:read', { groupId, message_id: message.id });
        }

        // Handle notifications (with @mention check)
        // System messages should NOT trigger notifications
        if (message.type !== 'system' && handleGroupNotificationRef.current) {
          const shouldNotify = handleGroupNotificationRef.current(groupId, message);
          if (shouldNotify) {
            // Notification will be handled by the function
          }
        }
      };
      
      socket.on('group:message:new', handleGroupMessageNew);

      // Handle group:message:read event (for read receipts)
      const handleGroupMessageRead = (data) => {
        const { groupId, message_id, userId, read_at, readCount, totalMembers, allRead } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✓✓ [Socket Client] group:message:read received:', { message_id, userId, readCount, totalMembers, allRead });
        }
        
        // If current user read the message, update group unreadCount
        if (userId === user?.id && groupId) {
          setGroups((prev) =>
            prev.map((group) => {
              if (group.id === groupId && group.unreadCount > 0) {
                // Decrease unreadCount when user reads a message
                return {
                  ...group,
                  unreadCount: Math.max(0, (group.unreadCount || 0) - 1),
                };
              }
              return group;
            })
          );
        }
        
        // Update read status for this message
        setGroupMessageReads((prev) => ({
          ...prev,
          [message_id]: {
            readCount,
            totalMembers,
            allRead,
            readers: prev[message_id]?.readers || [],
          },
        }));
      };
      
      // Handle group:message:read:ack event (confirmation that we marked a group message as read)
      const handleGroupMessageReadAck = (data) => {
        const { groupId, message_id, read_at, unreadCount } = data;
        // Development-only logging
        if (process.env.NODE_ENV === 'development') {
          console.log('✓✓ [Socket Client] group:message:read:ack received:', { groupId, message_id, read_at, unreadCount });
        }
        
        // CRITICAL: Update group unreadCount immediately if provided by backend
        // This ensures badge updates in real-time when messages are read
        if (groupId && typeof unreadCount === 'number') {
          setGroups((prev) =>
            prev.map((group) => {
              if (group.id === groupId) {
                console.log(`🔄 [Sync] Updating group unreadCount from ${group.unreadCount} to ${unreadCount} for group: ${groupId}`);
                return {
                  ...group,
                  unreadCount: Math.max(0, unreadCount), // Ensure non-negative
                };
              }
              return group;
            })
          );
        }
        
        // Update local state - mark the group message we read as read
        setGroupMessages((prev) => {
          const updated = { ...prev };
          if (updated[groupId]) {
            updated[groupId] = updated[groupId].map((msg) => {
              // Update if message is NOT from current user (messages we received and read)
              if (msg.id === message_id && msg.senderId !== user?.id) {
                return { ...msg, isRead: true, read_at: read_at || new Date().toISOString() };
              }
              return msg;
            });
          }
          return updated;
        });
      };
      
      // Handle group:unread-count:updated event - Real-time badge update
      const handleGroupUnreadCountUpdated = (data) => {
        const { groupId, unreadCount } = data;
        if (!groupId || typeof unreadCount !== 'number') return;
        
        console.log(`🔄 [Sync] group:unread-count:updated received for group ${groupId}, unreadCount: ${unreadCount}`);
        
        // CRITICAL: Update group unreadCount immediately
        setGroups((prev) =>
          prev.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                unreadCount: Math.max(0, unreadCount), // Ensure non-negative
              };
            }
            return group;
          })
        );
      };
      
      socket.on('group:message:read', handleGroupMessageRead);
      socket.on('group:message:read:ack', handleGroupMessageReadAck);
      socket.on('group:unread-count:updated', handleGroupUnreadCountUpdated);

      // Handle note:deleted event - Real-time cleanup of shared note references
      const handleNoteDeleted = (data) => {
        const { note_id, note_title, message } = data;
        console.log('🔄 [Socket Client] note:deleted received, cleaning up shared note references:', note_id);
        
        // Remove note_id, note_title, note_content, note_title_color from all messages
        // This prevents "Not Bulunamadı" errors when viewing shared notes in chat
        setMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages };
          let hasChanges = false;
          
          Object.keys(updatedMessages).forEach((friendId) => {
            const friendMessages = updatedMessages[friendId];
            const updatedFriendMessages = friendMessages.map((msg) => {
              // Check if message has shared note data matching deleted noteId
              if (msg.note_id === note_id || msg.noteId === note_id) {
                hasChanges = true;
                // Remove shared note fields but keep the message
                const { note_id: msgNoteId, noteId, note_title: msgNoteTitle, noteTitle, note_content: msgNoteContent, noteContent, note_title_color: msgNoteTitleColor, noteTitleColor, ...rest } = msg;
                return {
                  ...rest,
                  // Optionally add a fallback text if message becomes empty
                  content: msg.content || msg.text || message || i18n.t('chat:chatContext.sharedNoteDeletedFallback'),
                  type: msg.type === 'note_share' ? 'text' : msg.type || 'text',
                };
              }
              return msg;
            });
            
            if (hasChanges) {
              updatedMessages[friendId] = updatedFriendMessages;
            }
          });
          
          if (hasChanges) {
            console.log('✅ [Sync] Cleaned up shared note references from messages via socket');
          }
          
          return hasChanges ? updatedMessages : prevMessages;
        });
        
        // Also clean up group messages
        setGroupMessages((prevGroupMessages) => {
          const updatedGroupMessages = { ...prevGroupMessages };
          let hasChanges = false;
          
          Object.keys(updatedGroupMessages).forEach((groupId) => {
            const groupMessages = updatedGroupMessages[groupId];
            const updatedGroupMessagesList = groupMessages.map((msg) => {
              // Check if message has shared note data matching deleted noteId
              if (msg.note_id === note_id || msg.noteId === note_id) {
                hasChanges = true;
                // Remove shared note fields but keep the message
                const { note_id: msgNoteId, noteId, note_title: msgNoteTitle, noteTitle, note_content: msgNoteContent, noteContent, note_title_color: msgNoteTitleColor, noteTitleColor, ...rest } = msg;
                return {
                  ...rest,
                  // Optionally add a fallback text if message becomes empty
                  content: msg.content || msg.text || message || i18n.t('chat:chatContext.sharedNoteDeletedFallback'),
                  type: msg.type === 'note_share' ? 'text' : msg.type || 'text',
                };
              }
              return msg;
            });
            
            if (hasChanges) {
              updatedGroupMessages[groupId] = updatedGroupMessagesList;
            }
          });
          
          if (hasChanges) {
            console.log('✅ [Sync] Cleaned up shared note references from group messages via socket');
          }
          
          return hasChanges ? updatedGroupMessages : prevGroupMessages;
        });
      };
      
      socket.on('note:deleted', handleNoteDeleted);

      // Collab davet yanıtlandı — her iki tarafın mesajını güncelle
      const handleCollabInviteResponded = ({ messageId, accepted, groupId: gId }) => {
        const status = accepted ? 'accepted' : 'rejected';
        const mid = String(messageId);
        if (gId) {
          setGroupMessages(prev => {
            const updated = { ...prev };
            if (updated[gId]) {
              updated[gId] = updated[gId].map(msg =>
                String(msg.id) === mid ? { ...msg, collab_status: status } : msg
              );
            }
            return updated;
          });
        } else {
          setMessages(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(friendId => {
              updated[friendId] = updated[friendId].map(msg =>
                String(msg.id) === mid ? { ...msg, collab_status: status } : msg
              );
            });
            return updated;
          });
        }
        // Broadcast to any mounted CollabInviteMessage components (bypasses stale socket refs)
        window.dispatchEvent(new CustomEvent('notesplus-collab-invite-responded', {
          detail: { messageId: mid, status },
        }));
      };
      socket.on('collab-invite:responded', handleCollabInviteResponded);

      // Admin bildirim modalı / çan
      const handleAdminNotification = (data) => {
        if (data?.type === 'modal') {
          if (!isModalNotificationActive(data)) return;
          setPendingAdminNotification(data);
        } else if (data?.type === 'bell') {
          window.dispatchEvent(new CustomEvent('notesplus-admin-bell-notification', { detail: data }));
        }
      };

      // Admin geçmiş istatistik güncellemesi (seen/clicked/deleted)
      const handleAdminNotifStats = (data) => {
        window.dispatchEvent(new CustomEvent('notesplus-admin-notif-stats', { detail: data }));
      };
      socket.on('admin:notification:stats', handleAdminNotifStats);
      socket.on('admin:notification', handleAdminNotification);

      // Handle note:updated (ortak not içeriği güncellendi – sadece o notu güncelle, sayfa yenileme)
      const handleNoteUpdated = (data) => {
        const { noteId, note } = data || {};
        if (noteId && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-note-updated', { detail: { noteId, note } }));
        }
      };
      socket.on('note:updated', handleNoteUpdated);

      // Handle note:collab-updated (davet kabul edildi – sahibin listesini yenile, avatarlar görünsün)
      const handleNoteCollabUpdated = (data) => {
        const { noteId } = data || {};
        if (noteId && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-reload-notes', { detail: { noteId } }));
        }
      };
      socket.on('note:collab-updated', handleNoteCollabUpdated);

      // Handle group:typing event
      const handleGroupTyping = (data) => {
        const { groupId, from, from_email, from_name, from_avatar_url, isTyping } = data;
        
        setGroupTyping((prev) => {
          const currentTyping = prev[groupId] || [];
          
          if (isTyping) {
            // Add to typing list if not already there
            const exists = currentTyping.some((t) => t.userId === from);
            if (!exists) {
              return {
                ...prev,
                [groupId]: [...currentTyping, {
                  userId: from,
                  userName: from_name || from_email || i18n.t('chat:chatContext.unknownShort'),
                  avatarUrl: from_avatar_url || null,
                }],
              };
            }
          } else {
            // Remove from typing list
            return {
              ...prev,
              [groupId]: currentTyping.filter((t) => t.userId !== from),
            };
          }
          
          return prev;
        });
      };
      
      socket.on('group:typing', handleGroupTyping);

      // Handle group:system:message event (WhatsApp-style system messages)
      const handleGroupSystemMessage = (data) => {
        const { groupId, message } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔔 [Socket Client] group:system:message received:', message.id, 'in group', groupId);
        }
        
        // System messages are already handled by group:message:new
        // This event is for explicit system message notifications
        // Reload group messages to include the system message
        if (selectedGroupIdRef.current === groupId) {
          loadGroupMessages(groupId);
        }
      };
      
      socket.on('group:system:message', handleGroupSystemMessage);

      // Handle group:locked event (Plus group but owner is no longer Plus)
      const handleGroupLocked = (data) => {
        const { groupId, message } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔒 [Socket Client] group:locked received for group', groupId);
        }
        
        // Update group state to reflect lock
        setGroups((prev) =>
          prev.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                isLocked: true,
                lockMessage: message || i18n.t('chat:chatContext.groupLockedEventFallback'),
              };
            }
            return group;
          })
        );
        
        // Reload groups to ensure backend state is synced (for persistence)
        loadGroups(true);
      };
      
      socket.on('group:locked', handleGroupLocked);

      // Handle group:unlocked event (Owner became Plus again)
      const handleGroupUnlocked = (data) => {
        const { groupId } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔓 [Socket Client] group:unlocked received for group', groupId);
        }
        
        // Update group state to reflect unlock
        setGroups((prev) =>
          prev.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                isLocked: false,
                lockMessage: null,
              };
            }
            return group;
          })
        );
        
        // Reload groups to ensure backend state is synced
        loadGroups(true);
      };
      
      socket.on('group:unlocked', handleGroupUnlocked);

      // Handle group:destroyed event (Group was destroyed by owner)
      const handleGroupDestroyed = (data) => {
        const { groupId, group } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('💥 [Socket Client] group:destroyed received for group', groupId);
        }
        
        // Update group state to reflect destroyed state
        setGroups((prev) =>
          prev.map((g) => {
            if (g.id === groupId) {
              return {
                ...g,
                is_active: false,
                destroyed_at: group?.destroyed_at || new Date(),
              };
            }
            return g;
          })
        );
      };
      
      socket.on('group:destroyed', handleGroupDestroyed);

      // Handle group:left event (User left the group)
      const handleGroupLeft = (data) => {
        const { groupId } = data;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('👋 [Socket Client] group:left received for group', groupId);
        }
        
        // Remove group from state
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        
        // Clear messages for this group
        setGroupMessages((prev) => {
          const updated = { ...prev };
          delete updated[groupId];
          return updated;
        });
        
        // If this group was selected, deselect it
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
      };
      
      socket.on('group:left', handleGroupLeft);

      // Handle group:message:sent (find temp IN list, prefer temp contact fields so group card stays correct)
      const handleGroupMessageSent = async (data) => {
        const { groupId, message } = data;
        if (!message) return;

        let resolvedFileUrl = message.file_url || message.fileUrl || null;
        if (resolvedFileUrl && !resolvedFileUrl.startsWith('http')) {
          try {
            const apiBaseUrl = await getApiBaseUrlAsync();
            const baseUrl = apiBaseUrl.replace('/api', '');
            resolvedFileUrl = `${baseUrl}${resolvedFileUrl.startsWith('/') ? '' : '/'}${resolvedFileUrl}`;
          } catch (error) {
            console.warn('Failed to resolve group message file URL:', error);
          }
        }

        setGroupMessages((prev) => {
          const groupMsgs = prev[groupId] || [];
          const tempMsg = groupMsgs.find((m) => m.id?.startsWith('temp_'));
          const filtered = groupMsgs.filter((m) => m.id !== (tempMsg?.id) && m.id !== message.id);
          const contactUserId = tempMsg?.contact_user_id ?? message.contact_user_id ?? message.contactUserId ?? null;
          const contactName = tempMsg?.contact_name ?? message.contact_name ?? message.contactName ?? null;
          const contactAvatarUrl = tempMsg?.contact_avatar_url ?? message.contact_avatar_url ?? message.contactAvatarUrl ?? null;
          const contactEmail = tempMsg?.contact_email ?? message.contact_email ?? message.contactEmail ?? null;
          
          return {
            ...prev,
            [groupId]: [...filtered, {
              id: message.id,
              senderId: message.sender_id,
              senderEmail: message.sender_email,
              content: message.content,
              type: message.type || 'text',
              file_url: resolvedFileUrl,
              fileUrl: resolvedFileUrl,
              file_name: message.file_name || message.fileName,
              fileName: message.file_name || message.fileName,
              note_id: message.note_id || null,
              note_title: message.note_title || null,
              note_content: message.note_content || null,
              note_title_color: message.note_title_color || null,
              event_id: message.event_id || null,
              event_title: message.event_title || null,
              event_start: message.event_start || null,
              event_end: message.event_end || null,
              event_all_day: message.event_all_day || null,
              event_description: message.event_description || null,
              event_location: message.event_location || null,
              poll_id: message.poll_id || null,
              poll_title: message.poll_title || null,
              poll_multiple_choice: message.poll_multiple_choice || null,
              contact_user_id: contactUserId,
              contact_name: contactName,
              contact_avatar_url: contactAvatarUrl,
              contact_email: contactEmail,
              timestamp: message.created_at,
              replyTo: message.replyTo || null,
              isForwarded: message.isForwarded || false,
              forwardedFromMessageId: message.forwardedFromMessageId || null,
              forwardedFromSenderId: message.forwardedFromSenderId || null,
              mentionedUserIds: message.mentionedUserIds || [],
            }],
          };
        });
      };
      
      socket.on('group:message:sent', handleGroupMessageSent);

      // Handle message:deleted_for_everyone (DM)
      const handleMessageDeletedForEveryone = ({ messageId, deletedByName }) => {
        setMessages((prev) => {
          const updated = {};
          let changed = false;
          for (const [fid, msgs] of Object.entries(prev)) {
            const next = msgs.map((m) => {
              if (m.id === messageId && !m.is_deleted_for_everyone) {
                changed = true;
                return { ...m, is_deleted_for_everyone: true, deleted_by_name: deletedByName };
              }
              return m;
            });
            updated[fid] = next;
          }
          return changed ? updated : prev;
        });
      };
      socket.on('message:deleted_for_everyone', handleMessageDeletedForEveryone);

      // Handle group:message:deleted_for_everyone
      const handleGroupMessageDeletedForEveryone = ({ groupId, messageId, deletedByName }) => {
        setGroupMessages((prev) => {
          const msgs = prev[groupId];
          if (!msgs) return prev;
          const updated = msgs.map((m) => {
            if (m.id === messageId && !m.is_deleted_for_everyone) {
              return { ...m, is_deleted_for_everyone: true, deleted_by_name: deletedByName };
            }
            return m;
          });
          return { ...prev, [groupId]: updated };
        });
      };
      socket.on('group:message:deleted_for_everyone', handleGroupMessageDeletedForEveryone);

        // Store handlers in ref for cleanup
        socketRef.current._handlers = {
          handleUserStatus,
        handleGroupSystemMessage,
        handleGroupLocked,
        handleGroupUnlocked,
        handleGroupDestroyed,
        handleGroupLeft,
        handleMessageNew,
        handleMessageDelivered,
        handleMessageRead,
        handleMessageReadAck,
        handleFriendsUpdated,
        handleUnreadCountUpdated,
        handleTyping,
        handleUserProfileUpdated,
        handleUserAvatarUpdated,
        handleFriendRequest,
        handleFriendAccepted,
        handleFriendCancelled,
        handleGroupCreated,
        handleGroupUpdated,
        handleGroupDeleted,
        handleGroupMemberAdded,
        handleGroupMemberRemoved,
        handleGroupMemberRoleChanged,
        handleGroupMemberLimitReached,
        handleGroupMessageNew,
        handleGroupMessageRead,
        handleGroupMessageReadAck,
        handleGroupUnreadCountUpdated,
        handleGroupTyping,
        handleGroupMessageSent,
        handleNoteDeleted,
        handleNoteUpdated,
        handleNoteCollabUpdated,
        handleCollabInviteResponded,
        handleAdminNotification,
        handleAdminNotifStats,
        handleEventResponse,
        handleEventUpdated,
        handlePollVoted,
        handleMessageDeletedForEveryone,
        handleGroupMessageDeletedForEveryone,
      };
      }

      return () => {
      const socket = socketRef.current;
      if (socket && socket._handlers) {
        // Remove all socket listeners using named handlers
        const handlers = socket._handlers || {};
        socket.off('user:status', handlers.handleUserStatus);
        socket.off('message:new', handlers.handleMessageNew);
        socket.off('message:delivered', handlers.handleMessageDelivered);
        socket.off('message:read', handlers.handleMessageRead);
        socket.off('message:read:ack', handlers.handleMessageReadAck);
        socket.off('friends:updated', handlers.handleFriendsUpdated);
        socket.off('unread-count:updated', handlers.handleUnreadCountUpdated);
        socket.off('typing', handlers.handleTyping);
        socket.off('user:profile:updated', handlers.handleUserProfileUpdated);
        socket.off('user:avatar:updated', handlers.handleUserAvatarUpdated);
        socket.off('friend:request', handlers.handleFriendRequest);
        socket.off('friend:accepted', handlers.handleFriendAccepted);
        socket.off('friend:cancelled', handlers.handleFriendCancelled);
        // Group events
        socket.off('group:created', handlers.handleGroupCreated);
        socket.off('group:updated', handlers.handleGroupUpdated);
        socket.off('group:deleted', handlers.handleGroupDeleted);
        socket.off('group:member:added', handlers.handleGroupMemberAdded);
        socket.off('group:member:removed', handlers.handleGroupMemberRemoved);
        socket.off('group:member:limit-reached', handlers.handleGroupMemberLimitReached);
        socket.off('group:message:new', handlers.handleGroupMessageNew);
        socket.off('group:message:read', handlers.handleGroupMessageRead);
        socket.off('group:message:read:ack', handlers.handleGroupMessageReadAck);
        socket.off('group:unread-count:updated', handlers.handleGroupUnreadCountUpdated);
        socket.off('group:message:sent', handlers.handleGroupMessageSent);
        socket.off('event:response', handlers.handleEventResponse);
        socket.off('event:updated', handlers.handleEventUpdated);
        socket.off('poll:voted', handlers.handlePollVoted);
        socket.off('group:typing', handlers.handleGroupTyping);
        socket.off('note:deleted', handlers.handleNoteDeleted);
        socket.off('note:updated', handlers.handleNoteUpdated);
        socket.off('note:collab-updated', handlers.handleNoteCollabUpdated);
        socket.off('collab-invite:responded', handlers.handleCollabInviteResponded);
        socket.off('admin:notification', handlers.handleAdminNotification);
        socket.off('admin:notification:stats', handlers.handleAdminNotifStats);
        socket.off('message:deleted_for_everyone', handlers.handleMessageDeletedForEveryone);
        socket.off('group:message:deleted_for_everyone', handlers.handleGroupMessageDeletedForEveryone);
        socket.off('group:system:message', handlers.handleGroupSystemMessage);
        socket.off('group:locked', handlers.handleGroupLocked);
        socket.off('group:unlocked', handlers.handleGroupUnlocked);
        socket.off('group:destroyed', handlers.handleGroupDestroyed);
        socket.off('group:left', handlers.handleGroupLeft);
        
        // Clear handlers ref
        delete socket._handlers;
      }
      
      // CRITICAL: Only disconnect if user is logging out (not on route changes)
      // Don't disconnect on cleanup - let socket stay connected
      // Only disconnect when explicitly needed (logout)
      
      // Cleanup offline timeouts
      Object.values(offlineTimeoutsRef.current).forEach((timeoutId) => {
        if (timeoutId) clearTimeout(timeoutId);
      });
      offlineTimeoutsRef.current = {};
      
      // Clear status cache
      friendStatusCacheRef.current = {};
    };
    }

    if (!socketManager.isConnected()) {
      socketManager.init(authToken).catch(() => {});
    }
  }, [isAuthenticated, authToken, socketConnectionState]);

  // Logout event'ini dinle ve state'i temizle
  useEffect(() => {
    const handleLogout = () => {
      // Disconnect socket
      if (socketRef.current) {
        disconnectSocket();
        socketRef.current = null;
      }
      
      // Tüm state'i temizle
      setFriends([]);
      setMessages({});
      setSelectedFriendId(null);
      setSearchQuery('');
      setUnreadCount(0);
      setHasUnreadMessages(false);
      setIsLoading(false);
      
      // Ref'leri temizle
      typingTimeoutsRef.current = {};
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-user-logged-out', handleLogout);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-user-logged-out', handleLogout);
      }
    };
  }, []);

  const selectedFriend = useMemo(
    () => friends.find((f) => (f.id === selectedFriendId || f.email === selectedFriendId)) || null,
    [friends, selectedFriendId]
  );

  const selectedFriendMessages = useMemo(
    () => (selectedFriendId ? messages[selectedFriendId] || [] : []),
    [messages, selectedFriendId]
  );

  const filteredFriends = useMemo(() => {
    let filtered = friends;
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = friends.filter(
        (friend) =>
          (friend.name && friend.name.toLowerCase().includes(query)) ||
          (friend.email && friend.email.toLowerCase().includes(query))
      );
    }
    
    // For deleted friends, ensure lastMessageTime is preserved
    // If lastMessageTime is missing but messages exist, set it from messages
    filtered = filtered.map((friend) => {
      if (friend.isDeleted === true) {
        const friendId = friend.email || friend.id;
        // If lastMessageTime is missing, try to get it from messages
        if (!friend.lastMessageTime) {
          const friendMessages = messages[friendId] || [];
          if (friendMessages.length > 0) {
            // Get last message timestamp
            const lastMessage = friendMessages[friendMessages.length - 1];
            if (lastMessage && lastMessage.timestamp) {
              return {
                ...friend,
                lastMessageTime: lastMessage.timestamp,
                lastMessage: lastMessage.content || lastMessage.file_name || friend.lastMessage || '',
              };
            }
          }
        }
        // If lastMessageTime exists, keep it (it was set when friend was deleted)
        return friend;
      }
      return friend;
    });
    
    // Show friends that have messages (lastMessageTime exists) OR are deleted with lastMessageTime
    // Deleted friends are kept in list to show message history if they have lastMessageTime
    filtered = filtered.filter((friend) => {
      if (friend.isDeleted === true) {
        // For deleted friends, show if they have lastMessageTime (set when deleted or from messages)
        return friend.lastMessageTime !== null;
      }
      return friend.lastMessageTime !== null;
    });
    
    // Sort: pinned friends first (newer pins first), then by last message time (most recent first)
    return [...filtered].sort((a, b) => {
      // Pinned friends come first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      
      // If both pinned, sort by pinned_at (newer pins first - most recently pinned at top)
      if (a.pinned && b.pinned) {
        if (a.pinnedAt && b.pinnedAt) {
          return new Date(b.pinnedAt) - new Date(a.pinnedAt); // Newer pinned first
        }
        if (a.pinnedAt) return -1; // a has pinnedAt, b doesn't - a comes first
        if (b.pinnedAt) return 1; // b has pinnedAt, a doesn't - b comes first
        // Neither has pinnedAt, fall through to lastMessageTime
      }
      
      // Both not pinned or same pin status: sort by lastMessageTime (most recent first)
      if (a.lastMessageTime && b.lastMessageTime) {
        return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
      }
      if (a.lastMessageTime) return -1;
      if (b.lastMessageTime) return 1;
      
      // Fallback: maintain original order (by name or email)
      return (a.name || a.email || '').localeCompare(b.name || b.email || '', i18n.language);
    });
  }, [friends, searchQuery, messages, i18n.language]);

  // Load friends from backend with debounce and socket check
  const loadFriends = useCallback(async (skipDebounce = false) => {
    if (!isAuthenticated || !user?.id || !authToken) return;
    
    // Clear existing timer
    if (loadFriendsTimerRef.current) {
      clearTimeout(loadFriendsTimerRef.current);
      loadFriendsTimerRef.current = null;
    }
    
    const executeLoad = async (forceLoad = false) => {
      // If forceLoad is true (skipDebounce), always load (for manual refresh or state sync)
      // Otherwise, skip if socket already provided data
      if (!forceLoad && socketDataReceivedRef.current.friends && socketRef.current?.connected) {
        return;
      }

      try {
        setIsLoading(true);
        const result = await chatApi.getFriends({ token: authToken });
        if (result?.ok && result?.friends) {
          socketDataReceivedRef.current.friends = true;
        // WhatsApp-like status system: Server returns is_online and last_seen
        // No client-side calculation - just use what server provides
        const friendsWithStatus = result.friends.map((friend) => {
          let statusText = i18n.t('chat:chatContext.onlineStatus');
          let statusDot = 'green';

          // Server provides is_online (boolean) and last_seen (datetime or null)
          if (friend.is_online === true) {
            statusText = i18n.t('chat:chatContext.onlineStatus');
            statusDot = 'green';
          } else {
            // Offline - show last seen time using formatLastSeen function
            if (friend.last_seen) {
              const formatted = formatLastSeen(friend.last_seen);
              statusText = formatted || '';
            } else {
              statusText = '';
            }
            statusDot = 'gray';
          }
          
          // Normalize pinned value to boolean (backend might return 0/1 or true/false)
          const pinned = friend.pinned === true || friend.pinned === 1 || friend.pinned === '1' || friend.pinned === 'true';
          
          return {
            ...friend,
            id: friend.email, // Use email as ID (mail-based system)
            status: friend.is_online === true ? 'online' : 'offline',
            statusText,
            statusDot,
            pinned, // Ensure pinned is always boolean
          };
        });
        
        // Preserve deleted friends (keep them in list for message history)
        setFriends((prevFriends) => {
          const deletedFriends = prevFriends.filter(f => f.isDeleted === true);
          const deletedFriendsMap = new Map();
          deletedFriends.forEach(f => {
            const friendId = f.email || f.id;
            deletedFriendsMap.set(friendId, f);
          });
          
          // Create a map of previous friends to preserve pinned state
          const prevFriendsMap = new Map();
          prevFriends.forEach(f => {
            const friendId = f.email || f.id;
            prevFriendsMap.set(friendId, f);
          });
          
          // Merge: add deleted friends back if they're not in new list
          // Also preserve pinned state from previous state if we just updated it (race condition protection)
          const mergedFriends = friendsWithStatus.map((friend) => {
            const friendId = friend.email || friend.id;
            const prevFriend = prevFriendsMap.get(friendId);
            const recentPinUpdate = recentPinUpdatesRef.current[friendId];
            
            // Priority 1: If we have a recent pin update (within last 10 seconds), use that
            if (recentPinUpdate && (Date.now() - recentPinUpdate.timestamp) < 10000) {
              return {
                ...friend,
                pinned: recentPinUpdate.pinned,
                pinnedAt: recentPinUpdate.pinnedAt,
              };
            }
            
            // Priority 2: Preserve pinned state from previous state if we just updated it
            // This prevents race condition where polling overwrites our recent pin/unpin action
            if (prevFriend && prevFriend.pinnedAt) {
              const prevPinnedAt = new Date(prevFriend.pinnedAt).getTime();
              const newPinnedAt = friend.pinnedAt ? new Date(friend.pinnedAt).getTime() : 0;
              
              // If previous pinnedAt is newer (more recent), it means we just updated it
              // and backend hasn't synced yet, so preserve previous state
              if (prevPinnedAt > newPinnedAt) {
                return {
                  ...friend,
                  pinned: prevFriend.pinned === true || prevFriend.pinned === 1 || prevFriend.pinned === '1' || prevFriend.pinned === 'true',
                  pinnedAt: prevFriend.pinnedAt,
                };
              }
            }
            
            return friend;
          });
          
          deletedFriendsMap.forEach((deletedFriend, friendId) => {
            const exists = mergedFriends.find(f => {
              const fId = f.email || f.id;
              return fId === friendId;
            });
            if (!exists) {
              // Keep deleted friend in list for message history
              // Preserve lastMessageTime and lastMessage from previous state
              // These are already set when friend was deleted, so keep them
              // Also check current messages state as fallback
              const friendId = deletedFriend.email || deletedFriend.id;
              if (!deletedFriend.lastMessageTime) {
                // Try to get from current messages state
                const currentMessages = messages[friendId] || [];
                if (currentMessages.length > 0) {
                  const lastMessage = currentMessages[currentMessages.length - 1];
                  if (lastMessage && lastMessage.timestamp) {
                    deletedFriend.lastMessageTime = lastMessage.timestamp;
                    deletedFriend.lastMessage = lastMessage.content || lastMessage.file_name || '';
                  }
                }
              }
              mergedFriends.push(deletedFriend);
            }
          });
          
          return mergedFriends;
        });
        }
      } catch (error) {
      // Error handling
      socketDataReceivedRef.current.friends = false;
    } finally {
      setIsLoading(false);
    }
    };
    
    // Debounce: wait 1 second before executing (unless skipDebounce is true)
    if (skipDebounce) {
      await executeLoad(true); // CRITICAL FIX: Pass forceLoad=true to bypass socket data check
    } else {
      loadFriendsTimerRef.current = setTimeout(() => executeLoad(false), 1000);
    }
  }, [user?.id, authToken, location.pathname, messages, isAuthenticated]);

  // Normalize API message to client shape (shared by full load and silent sync)
  const normalizeApiMessages = useCallback((resultMessages, baseUrl) => {
    return (resultMessages || []).map((msg) => {
      let fileUrl = msg.file_url || msg.fileUrl;
      if (fileUrl && !fileUrl.startsWith('http') && baseUrl) {
        fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }
      return {
        id: msg.id,
        senderId: msg.sender_id === user?.id ? 'current_user' : msg.sender_id,
        receiverId: msg.receiver_id,
        content: msg.content,
        type: msg.type || 'text',
        file_url: fileUrl,
        file_name: msg.file_name || msg.fileName,
        note_id: msg.note_id || null,
        note_title: msg.note_title || null,
        note_content: msg.note_content || null,
        note_title_color: msg.note_title_color || null,
        event_id: msg.event_id || null,
        event_title: msg.event_title || null,
        event_start: msg.event_start || null,
        event_end: msg.event_end || null,
        event_all_day: msg.event_all_day || null,
        event_description: msg.event_description || null,
        poll_id: msg.poll_id || null,
        poll_title: msg.poll_title || null,
        poll_multiple_choice: msg.poll_multiple_choice || null,
        contact_user_id: msg.contact_user_id ?? msg.contactUserId ?? null,
        contact_name: msg.contact_name ?? msg.contactName ?? null,
        contact_avatar_url: msg.contact_avatar_url ?? msg.contactAvatarUrl ?? null,
        contact_email: msg.contact_email ?? msg.contactEmail ?? null,
        timestamp: msg.created_at,
        isRead: msg.is_read,
        delivered_at: msg.delivered_at || null,
        read_at: msg.read_at || null,
        replyTo: msg.replyTo || null,
        isForwarded: msg.isForwarded || false,
        forwardedFromMessageId: msg.forwardedFromMessageId || null,
        forwardedFromSenderId: msg.forwardedFromSenderId || null,
        reactions: msg.reactions || {},
        is_deleted_for_everyone: msg.is_deleted_for_everyone || false,
        deleted_by_name: msg.deleted_by_name || null,
      };
    });
  }, [user?.id]);

  // Load messages for a friend — smart cache: show cache instantly, then silent sync with since
  const loadMessages = useCallback(async (friendId, options = {}) => {
    if (!isAuthenticated || !user?.id || !authToken || !friendId) return;

    // Abort any previous in-flight fetch to prevent stale responses overwriting current view
    if (loadMessageAbortRef.current) {
      loadMessageAbortRef.current.abort();
    }
    const abortController = new AbortController();
    loadMessageAbortRef.current = abortController;

    const cached = messagesRef.current[friendId] || [];
    const hasCache = cached.length > 0;
    const forceFullLoad = options.forceFullLoad === true;

    let baseUrl = '';
    try {
      const apiBaseUrl = await getApiBaseUrlAsync();
      baseUrl = apiBaseUrl.replace('/api', '');
    } catch (error) {
      try {
        const apiBaseUrl = getApiBaseUrl();
        if (apiBaseUrl && typeof apiBaseUrl === 'string') baseUrl = apiBaseUrl.replace('/api', '');
      } catch (_) {}
    }

    // No cache (or force full): show loading, full fetch
    if (!hasCache || forceFullLoad) {
      setLoadingMessagesForFriendId(friendId);
    }

    try {
      const sinceParam = hasCache && !forceFullLoad && cached.length > 0
        ? cached[cached.length - 1].timestamp
        : null;

      const result = await chatApi.getMessages({ token: authToken, friendId, since: sinceParam, signal: abortController.signal });
      if (!result?.ok || !result.messages) {
        if (!hasCache) setLoadingMessagesForFriendId(null);
        return;
      }

      const newMessages = normalizeApiMessages(result.messages, baseUrl);

      if (hasCache && !forceFullLoad && sinceParam) {
        // Smart reconciliation: never full replace; update existing by id, append only truly new; no sort to avoid layout shift
        setMessages((prev) => {
          const existing = prev[friendId] || [];
          const existingIds = new Set(existing.map((m) => m.id));
          let hasChanges = false;
          const merged = existing.map((msg) => {
            const fromApi = newMessages.find((m) => m.id === msg.id);
            if (fromApi) {
              hasChanges = true;
              // Merge only updatable fields (status/delivery/reactions); keep same object ref for unchanged so React keeps DOM
              return {
                ...msg,
                isRead: fromApi.isRead ?? msg.isRead,
                delivered_at: fromApi.delivered_at ?? msg.delivered_at,
                read_at: fromApi.read_at ?? msg.read_at,
                reactions: fromApi.reactions ?? msg.reactions ?? {},
              };
            }
            return msg;
          });
          const toAppend = newMessages.filter((m) => !existingIds.has(m.id));
          if (toAppend.length > 0) {
            hasChanges = true;
            merged.push(...toAppend);
          }
          if (!hasChanges) return prev;
          return { ...prev, [friendId]: merged };
        });
      } else {
        // Full load: merge with any socket messages that arrived during the API call
        setMessages((prev) => {
          const existing = prev[friendId] || [];
          if (existing.length === 0) {
            return { ...prev, [friendId]: newMessages };
          }
          const apiIds = new Set(newMessages.map((m) => m.id));
          const socketExtra = existing.filter(
            (m) => !String(m.id).startsWith('temp_') && !apiIds.has(m.id)
          );
          if (socketExtra.length === 0) {
            return { ...prev, [friendId]: newMessages };
          }
          const merged = [...newMessages, ...socketExtra].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
          return { ...prev, [friendId]: merged };
        });
      }
    } catch (error) {
      // Ignore abort errors — caused by intentional cancellation when friend changes
      if (error?.name === 'AbortError' || error?.message === 'The user aborted a request.') return;
      console.error('Load messages error:', error);
    } finally {
      // Only clear loading state if this is still the active fetch
      if (loadMessageAbortRef.current === abortController) {
        setLoadingMessagesForFriendId(null);
      }
    }
  }, [isAuthenticated, user?.id, authToken, normalizeApiMessages]);

  // Load unread count with debounce and socket check
  const loadUnreadCount = useCallback(async (skipDebounce = false) => {
    if (!isAuthenticated || !user?.id || !authToken) return;
    
    // Clear existing timer
    if (loadUnreadCountTimerRef.current) {
      clearTimeout(loadUnreadCountTimerRef.current);
      loadUnreadCountTimerRef.current = null;
    }
    
    const executeLoad = async () => {
      // Skip API call if socket has already provided unread count
      // (socket handles real-time updates, API is only fallback)
      if (socketDataReceivedRef.current.unreadCount && socketRef.current?.connected) {
        return;
      }

      try {
        const result = await chatApi.getUnreadCount({ token: authToken });
        if (result?.ok && result?.count !== undefined) {
          setUnreadCount(result.count);
          socketDataReceivedRef.current.unreadCount = true;
          // Set hasUnreadMessages based on unread count and current location
          const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
          setHasUnreadMessages(!isOnChatPage && result.count > 0);
        }
      } catch (error) {
        // Error handling
        socketDataReceivedRef.current.unreadCount = false;
      }
    };
    
    // Debounce: wait 500ms before executing (unless skipDebounce is true)
    if (skipDebounce) {
      await executeLoad();
    } else {
      loadUnreadCountTimerRef.current = setTimeout(executeLoad, 500);
    }
  }, [user?.id, authToken, location.pathname, isAuthenticated]);

  // Load pending friend request count with debounce
  const loadPendingFriendRequestCount = useCallback(async (skipDebounce = false) => {
    if (!isAuthenticated || !user?.id || !authToken) return;
    
    // Clear existing timer
    if (loadPendingFriendRequestCountTimerRef.current) {
      clearTimeout(loadPendingFriendRequestCountTimerRef.current);
      loadPendingFriendRequestCountTimerRef.current = null;
    }
    
    const executeLoad = async () => {
      try {
        const result = await chatApi.getIncomingRequests({ token: authToken });
        if (result?.ok && Array.isArray(result?.requests)) {
          setPendingFriendRequestCount(result.requests.length);
          socketDataReceivedRef.current.pendingRequests = true;
        }
      } catch (error) {
        // Error handling
        socketDataReceivedRef.current.pendingRequests = false;
      }
    };
    
    // Debounce: wait 1 second before executing (unless skipDebounce is true)
    if (skipDebounce) {
      await executeLoad();
    } else {
      loadPendingFriendRequestCountTimerRef.current = setTimeout(executeLoad, 1000);
    }
  }, [user?.id, authToken, isAuthenticated]);

  // Track previous unread count to detect new messages
  const prevUnreadCountRef = useRef(0);
  const lastNotifiedMessageRef = useRef({}); // { friendId: messageId }
  const friendsRef = useRef(friends);
  const selectedFriendIdRef = useRef(selectedFriendId);
  const locationRef = useRef(location.pathname); // Ref to store current location pathname
  const handleChatNotificationRef = useRef(null); // Ref to store handleChatNotification function

  // Update refs when values change
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);

  useEffect(() => {
    selectedFriendIdRef.current = selectedFriendId;
  }, [selectedFriendId]);

  // Update location ref when pathname changes (from React Router)
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Global Location Tracking: App.js'den gelen location güncellemelerini dinle (zorla senkronize)
  useEffect(() => {
    const handleLocationUpdate = (event) => {
      const { pathname } = event.detail || {};
      if (pathname && typeof pathname === 'string') {
        locationRef.current = pathname;
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('notesplus-location-update', handleLocationUpdate);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('notesplus-location-update', handleLocationUpdate);
      }
    };
  }, []);

  // Handle chat notification - YENİ YAKLAŞIM: Presence state'e göre karar ver (ASLA cache'leme)
  const handleChatNotification = useCallback(async (friendId, message) => {
    console.log('📨 YENİ MESAJ GELDİ, KONTROLLER BAŞLIYOR...', { friendId, messageId: message.id });
    
    // Emergency Sound Check: Hem locationRef hem de window.location.pathname kontrolü
    const locationRefPath = locationRef.current;
    const windowLocationPath = typeof window !== 'undefined' && window.location ? 
      (window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname) : 
      null;
    
    // Use window.location.pathname if available (more reliable), otherwise fallback to locationRef
    const currentPath = windowLocationPath || locationRefPath;
    
    const selectedFriendIdValue = selectedFriendIdRef.current;
    const isUserInDifferentTab = currentPath !== '/chat' && !currentPath.startsWith('/chat');
    const isDifferentUser = friendId !== selectedFriendIdValue;
    const shouldPlaySound = isUserInDifferentTab || isDifferentUser;
    
    console.log('🔔 Bildirim Kontrolü:', {
      locationRefPath,
      windowLocationPath,
      mevcutKonum: currentPath,
      mesajGonderen: friendId,
      seciliKisi: selectedFriendIdValue,
      sesCalmaliMi: shouldPlaySound,
      isUserInDifferentTab,
      isDifferentUser,
    });

    // Duplicate kontrolü: Aynı mesaj için birden fazla bildirim gösterilmesin
    if (lastNotifiedMessageRef.current[friendId] === message.id) {
      return;
    }

    const friend = friendsRef.current.find((f) => (f.id === friendId || f.email === friendId));
    if (!friend) return;

    // Check if friend is muted
    if (friend.mutedUntil) {
      const mutedUntil = new Date(friend.mutedUntil);
      const now = new Date();
      if (mutedUntil > now) {
        return;
      }
    }

    // CRITICAL: ASLA cache'leme - her çağrıda fresh state al (ref'ten)
    // (currentPath ve isUserInDifferentTab zaten yukarıda tanımlandı, tekrar tanımlamaya gerek yok)
    const isOnChatPage = !isUserInDifferentTab;
    
    // Presence state'i kontrol et (document.hidden, window focus durumu)
    const isFocused = !document.hidden && document.hasFocus();
    const isInChatAndFocused = isOnChatPage && isFocused;
    
    // Windows bildirimi kararı: sadece uygulama ön planda değilse
    const shouldShowWindowsNotification = !isFocused || document.hidden;

    // Get display name for notifications
    const getDisplayName = (friend) => {
      if (friend?.nickname && friend.nickname.trim() !== '') {
        return friend.nickname;
      }
      if (friend?.name && friend.name.trim() !== '') {
        return friend.name;
      }
      if (friend?.email) {
        const emailPrefix = friend.email.split('@')[0];
        return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
      }
      return i18n.t('chat:chatContext.unknownUserFull');
    };

    // Windows bildirimleri KALDIRILDI - sadece taskbar badge ve ses kullanılıyor

    // 2️⃣ Ses kararı
    // Kesin Kurallar:
    // - isUserInDifferentTab: Kullanıcı /chat sayfasında değilse (Notlar, Takvim vb.)
    // - isDifferentUser: Mesaj o an açık olan kişiden (selectedFriendId) farklı birinden geliyorsa
    // Ses çalma şartı: isUserInDifferentTab VEYA isDifferentUser → SES ÇAL
    // Sadece kullanıcı /chat yolundayken VE o an konuştuğu kişiden mesaj gelirse sessiz kalmalı
    const isMuted = preferences?.chatNotificationsMuted === true;
    if (!isMuted && preferences) {
      try {
        // Ses çalma şartı: Kullanıcı başka sekmedeyse VEYA mesaj farklı birinden geldiyse
        if (shouldPlaySound) {
          if (isOnChatPage && isFocused) {
            // Chat sayfasındayken ve focused → sadece newmessagesound.wav
            await playChatSound('newmessagesound');
          } else {
            // Chat sayfasında değilken veya unfocused → seçilen bildirim sesi
            // Plus üyeler için varsayılan olarak Plus ses preset'i kullan
            let selectedSound = preferences.chatNotificationSoundId;
            if (!selectedSound) {
              // Plus üye ise varsayılan Plus sesi, değilse standart ses
              selectedSound = isPlusUser ? 'plus-premium' : 'auramessage';
            }
            await playChatSound(selectedSound);
          }
        }
        // Eğer shouldPlaySound === false ise (kullanıcı /chat'ta VE o an açık kişiden mesaj geldi)
        // Ses çalmayacak - sessiz kalacak
      } catch (error) {
        console.warn('Chat ses çalınamadı:', error);
      }
    }

    // 3️⃣ Toast bildirimi (sadece chat dışındayken ve focused; skip when app lock active)
    if (!isOnChatPage && isFocused && !getLockActive(user?.id)) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-chat-toast', {
          detail: {
            friend,
            messageId: message.id,
          },
        }));
        lastNotifiedMessageRef.current[friendId] = message.id;
      }
    }

    // 4️⃣ Sidebar badge (her zaman gösterilir)
    lastNotifiedMessageRef.current[friendId] = message.id;

    // 5️⃣ Taskbar badge güncellemesi - mesaj geldiğinde zorla güncelle
    // NOT: Badge güncellemesi useEffect ile debounced olarak yapılıyor
    // Burada sadece log yazıyoruz, gerçek güncelleme useEffect tarafından yapılacak
    // (unreadCount state'i değiştiğinde useEffect tetiklenecek)
  }, [preferences, user?.id]); // location.pathname ve selectedFriendId artık ref'ten okunuyor

  // Update ref when handleChatNotification changes (so socket handler always uses latest version)
  useEffect(() => {
    handleChatNotificationRef.current = handleChatNotification;
  }, [handleChatNotification]);

  // Presence state güncellemesi - Tab değişimi
  useEffect(() => {
    if (typeof window !== 'undefined' && window.api?.updatePresence) {
      const isChatTabActive = location.pathname === '/chat' || location.pathname.startsWith('/chat');
      window.api.updatePresence({ isChatTabActive });
    }
  }, [location.pathname]);


  // Initial load friends and unread count (no polling - realtime via Socket.IO)
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !authToken) {
      return;
    }

    // Request notification permission for web
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        // Silent fail
      });
    }

    // Reset socket data flags on initial load
    socketDataReceivedRef.current = {
      unreadCount: false,
      friends: false,
      pendingRequests: false,
    };

    // Initial load only (skip debounce for initial load, Socket.IO handles realtime updates)
    loadFriends(true);
    loadUnreadCount(true);
    loadPendingFriendRequestCount(true);

    // Poll pending friend requests every 60 seconds (reduced from 30s)
    // Only poll if socket is not connected
    const intervalId = setInterval(() => {
      // Skip polling if socket is connected and we've received socket data
      if (socketRef.current?.connected && socketDataReceivedRef.current.pendingRequests) {
        return;
      }
      
      loadPendingFriendRequestCount();
    }, 60000); // 60 seconds (reduced frequency)

    return () => {
      clearInterval(intervalId);
      // Cleanup timers
      if (loadUnreadCountTimerRef.current) {
        clearTimeout(loadUnreadCountTimerRef.current);
      }
      if (loadPendingFriendRequestCountTimerRef.current) {
        clearTimeout(loadPendingFriendRequestCountTimerRef.current);
      }
      if (loadFriendsTimerRef.current) {
        clearTimeout(loadFriendsTimerRef.current);
      }
    };
  }, [isAuthenticated, user?.id, authToken, loadFriends, loadUnreadCount, loadPendingFriendRequestCount]);

  // CRITICAL: Listen for note deletion events to clean up shared note references in messages
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleNoteDeleted = (event) => {
      const { noteId } = event.detail;
      console.log('🔄 [Sync] Note deleted event received, cleaning up shared note references:', noteId);
      
      // Remove note_id, note_title, note_content, note_title_color from all messages
      // This prevents "Not Bulunamadı" errors when viewing shared notes in chat
      setMessages((prevMessages) => {
        const updatedMessages = { ...prevMessages };
        let hasChanges = false;
        
        Object.keys(updatedMessages).forEach((friendId) => {
          const friendMessages = updatedMessages[friendId];
          const updatedFriendMessages = friendMessages.map((message) => {
            // Check if message has shared note data matching deleted noteId
            if (message.note_id === noteId || message.noteId === noteId) {
              hasChanges = true;
              // Remove shared note fields but keep the message
              const { note_id, noteId, note_title, noteTitle, note_content, noteContent, note_title_color, noteTitleColor, ...rest } = message;
              return {
                ...rest,
                // Optionally add a fallback text if message becomes empty
                content: message.content || message.text || i18n.t('chat:chatContext.sharedNoteDeletedFallback'),
                type: message.type || 'text',
              };
            }
            return message;
          });
          
          if (hasChanges) {
            updatedMessages[friendId] = updatedFriendMessages;
          }
        });
        
        if (hasChanges) {
          console.log('✅ [Sync] Cleaned up shared note references from messages');
        }
        
        return hasChanges ? updatedMessages : prevMessages;
      });
      
      // Also clean up group messages
      setGroupMessages((prevGroupMessages) => {
        const updatedGroupMessages = { ...prevGroupMessages };
        let hasChanges = false;
        
        Object.keys(updatedGroupMessages).forEach((groupId) => {
          const groupMessages = updatedGroupMessages[groupId];
          const updatedGroupMessagesList = groupMessages.map((message) => {
            // Check if message has shared note data matching deleted noteId
            if (message.note_id === noteId || message.noteId === noteId) {
              hasChanges = true;
              // Remove shared note fields but keep the message
              const { note_id, noteId, note_title, noteTitle, note_content, noteContent, note_title_color, noteTitleColor, ...rest } = message;
              return {
                ...rest,
                // Optionally add a fallback text if message becomes empty
                content: message.content || message.text || i18n.t('chat:chatContext.sharedNoteDeletedFallback'),
                type: message.type || 'text',
              };
            }
            return message;
          });
          
          if (hasChanges) {
            updatedGroupMessages[groupId] = updatedGroupMessagesList;
          }
        });
        
        if (hasChanges) {
          console.log('✅ [Sync] Cleaned up shared note references from group messages');
        }
        
        return hasChanges ? updatedGroupMessages : prevGroupMessages;
      });
    };

    window.addEventListener('notesplus-note-deleted', handleNoteDeleted);
    
    return () => {
      window.removeEventListener('notesplus-note-deleted', handleNoteDeleted);
    };
  }, [isAuthenticated]);

  // Update prevUnreadCountRef when unreadCount changes
  useEffect(() => {
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  // Update taskbar badge when unreadCount changes (Windows only) - Debounced with count check
  // Includes both friend messages and group messages unread counts
  useEffect(() => {
    // Calculate total unread count: friend messages + group messages
    const groupUnreadCount = groups.reduce((sum, group) => sum + (group.unreadCount || 0), 0);
    const totalUnreadCount = unreadCount + groupUnreadCount;
    
    // Count check: Eğer totalUnreadCount değeri bir önceki gönderilen değerle aynıysa, tekrar gönderme
    if (lastBadgeCountRef.current === totalUnreadCount) {
      return; // Stale update - skip
    }

    // Clear existing timer
    if (badgeUpdateTimerRef.current) {
      clearTimeout(badgeUpdateTimerRef.current);
      badgeUpdateTimerRef.current = null;
    }

    if (typeof window !== 'undefined' && window.api && window.api.updateTaskbarBadge) {
      // Debounce: 300ms bekle, bu süre içinde yeni güncelleme gelirse eskisini iptal et
      badgeUpdateTimerRef.current = setTimeout(() => {
        try {
          console.log('🔔 ChatContext: updateTaskbarBadge çağrılıyor, totalUnreadCount:', totalUnreadCount, '(friend:', unreadCount, '+ group:', groupUnreadCount, ')');
          window.api.updateTaskbarBadge(totalUnreadCount);
          lastBadgeCountRef.current = totalUnreadCount; // Son gönderilen count'u kaydet
        } catch (error) {
          console.error('❌ Failed to update taskbar badge:', error);
        }
        badgeUpdateTimerRef.current = null;
      }, 300); // 300ms debounce
    }

    // Cleanup: Component unmount olduğunda veya unreadCount/groups değiştiğinde timer'ı temizle
    return () => {
      if (badgeUpdateTimerRef.current) {
        clearTimeout(badgeUpdateTimerRef.current);
        badgeUpdateTimerRef.current = null;
      }
    };
  }, [unreadCount, groups]);

  // Load messages when friend is selected
  useEffect(() => {
    if (selectedFriendId) {
      loadMessages(selectedFriendId);
    }
  }, [selectedFriendId, loadMessages]);

  const selectFriend = useCallback((friendId) => {
    // Plus limiti kontrolü - 6. pencere açılmaya çalışıldığında
    if (!isPlusUser && friendId) {
      const activeChatCount = Object.keys(messages).length;
      // Eğer bu friend için henüz mesaj yoksa, aktif chat sayısını kontrol et
      const isNewChat = !messages[friendId] || messages[friendId].length === 0;
      if (isNewChat && activeChatCount >= PlusLimits.CHAT_WINDOWS) {
        // Limit aşıldı - Gold bildirimi göster ve işlemi durdur
        showPlusGoldNotification(i18n.t('shared:plusGoldNotification.features.chat'), i18n.t('chat:chatContext.chatWindowLimitMessage'));
        return; // İşlemi durdur, chat açma
      }
    }

    // Clear group selection when selecting a friend
    setSelectedGroupId(null);
    setSelectedFriendId(friendId);
    // loadMessages(selectedFriendId) runs in useEffect when selectedFriendId changes — single source of truth
    if (friendId) {
      loadUnreadCount();
      
      // Clear chat star indicator when user opens a chat
      // This happens when user clicks on a friend that sent a message while app was in background
      setHasUnreadChatInBackground(false);
      
      // Mark messages as read via Socket.IO - debounced to prevent flood
      const socket = socketRef.current;
      if (socket && socket.connected) {
        // Use debounce to prevent multiple rapid calls
        const markAsReadTimeout = setTimeout(() => {
          const friendMessages = messages[friendId] || [];
          const messagesToMark = [];
          
          // Collect messages that need to be marked as read
          friendMessages.forEach((msg) => {
            if (msg.senderId !== 'current_user' && 
                !msg.isRead && 
                msg.id && 
                !msg.id.startsWith('temp_')) {
              messagesToMark.push(msg.id);
            }
          });

          // Optimistically update local state - mark messages as read immediately
          if (messagesToMark.length > 0) {
            setMessages((prev) => {
              const updated = { ...prev };
              if (updated[friendId]) {
                updated[friendId] = updated[friendId].map((msg) => {
                  if (messagesToMark.includes(msg.id) && msg.senderId !== 'current_user') {
                    return { ...msg, isRead: true };
                  }
                  return msg;
                });
              }
              return updated;
            });
            
            // CRITICAL: Update friend's unreadCount optimistically to 0
            // This ensures badge disappears immediately when chat is opened
            setFriends((prev) => {
              const updated = prev.map((friend) => {
                const friendIdMatch = friend.email || friend.id;
                if (friendIdMatch === friendId && friend.unreadCount > 0) {
                  console.log(`🔄 [Sync] Optimistically setting unreadCount to 0 for friend: ${friendId}`);
                  return { ...friend, unreadCount: 0 };
                }
                return friend;
              });
              
              // CRITICAL: Update global unread count immediately
              const totalUnread = updated.reduce((sum, f) => sum + (f.unreadCount || 0), 0);
              setUnreadCount(totalUnread);
              console.log(`✅ [Sync] Updated global unreadCount to: ${totalUnread}`);
              
              return updated;
            });
          }

          // Emit read events (batch if needed)
          messagesToMark.forEach((messageId) => {
            // Development-only logging
            if (process.env.NODE_ENV === 'development') {
              console.log('✓✓ [Socket Client] Emitting message:read for:', messageId);
            }
            socket.emit('message:read', { message_id: messageId });
          });
        }, 500); // 500ms debounce
        
        // Store timeout for potential cleanup
        if (!selectFriendMarkAsReadTimeoutRef.current) {
          selectFriendMarkAsReadTimeoutRef.current = {};
        }
        if (selectFriendMarkAsReadTimeoutRef.current[friendId]) {
          clearTimeout(selectFriendMarkAsReadTimeoutRef.current[friendId]);
        }
        selectFriendMarkAsReadTimeoutRef.current[friendId] = markAsReadTimeout;
      }
    }
  }, [loadMessages, loadUnreadCount, messages, isPlusUser]);

  // Update hasUnreadMessages when location changes or unreadCount changes
  // Includes both friend messages and group messages
  useEffect(() => {
    const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
    const groupUnreadCount = groups.reduce((sum, group) => sum + (group.unreadCount || 0), 0);
    const totalUnreadCount = unreadCount + groupUnreadCount;
    // If user is on chat page, hide indicator (they can see messages)
    // If user is not on chat page and has unread messages, show indicator
    setHasUnreadMessages(!isOnChatPage && totalUnreadCount > 0);
  }, [location.pathname, unreadCount, groups]);

  // Listen for external friend selection (e.g., from notification click)
  useEffect(() => {
    const handleSelectFriend = (event) => {
      const { friendId } = event.detail || {};
      if (friendId) {
        selectFriend(friendId);
      }
    };

    window.addEventListener('notesplus-select-chat-friend', handleSelectFriend);
    return () => {
      window.removeEventListener('notesplus-select-chat-friend', handleSelectFriend);
    };
  }, [selectFriend]);

  // Update taskbar badge when window gains focus and all messages are read
  useEffect(() => {
    const handleWindowFocus = () => {
      // Check if we're on chat page and have no unread messages (friend + group)
      const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
      const groupUnreadCount = groups.reduce((sum, group) => sum + (group.unreadCount || 0), 0);
      const totalUnreadCount = unreadCount + groupUnreadCount;
      if (isOnChatPage && totalUnreadCount === 0) {
        // All messages are read, clear badge
        if (typeof window !== 'undefined' && window.api && window.api.updateTaskbarBadge) {
          try {
            window.api.updateTaskbarBadge(0);
          } catch (error) {
            console.warn('Failed to clear taskbar badge on focus:', error);
          }
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleWindowFocus);
      return () => {
        window.removeEventListener('focus', handleWindowFocus);
      };
    }
  }, [location.pathname, unreadCount]);

  const sendMessage = useCallback(
    async (friendId, content, type = 'text', fileUrl = null, fileName = null, replyTo = null, forwardedFrom = null, eventPayload = null, pollPayload = null, contactPayload = null, notePayload = null) => {
      // AuthContext'ten gelen isAuthenticated kontrolü
      if (!isAuthenticated || !user?.id || !authToken) return;
      if (type === 'note_share') {
        if (!notePayload?.note_id || !notePayload?.note_title) return;
      } else if (type === 'event_share') {
        if (!eventPayload?.event_title || !eventPayload?.event_start) return;
      } else if (type === 'poll') {
        if (!pollPayload?.poll_title || !pollPayload?.poll_options || !Array.isArray(pollPayload.poll_options) || pollPayload.poll_options.length < 2) return;
      } else if (type === 'contact') {
        if (!contactPayload?.contact_user_id && !contactPayload?.contact_name) return;
      } else if (!content && !fileUrl) return;
      if (type === 'text' && (!content || !content.trim())) return;

      if (!socketManager.isConnected()) {
        await socketManager.forceReconnect();
      }
      const socket = socketManager.getSocket();
      if (!socket || !socket.connected) {
        try {
          const result = await chatApi.sendMessage({
            token: authToken,
            receiverId: friendId,
            content: content ? content.trim() : (type === 'event_share' ? i18n.t('chat:chatContext.eventShareContent', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollShareContent', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || '')),
            fileUrl: fileUrl,
            fileName: fileName,
            type,
            replyTo: replyTo || replyToMessage?.id || null,
            forwardedFrom: forwardedFrom || null,
            event_id: eventPayload?.event_id || null,
            event_title: eventPayload?.event_title || null,
            event_start: eventPayload?.event_start || null,
            event_end: eventPayload?.event_end || null,
            event_all_day: eventPayload?.event_all_day || false,
            event_description: eventPayload?.event_description || null,
            event_location: eventPayload?.event_location || null,
            poll_title: pollPayload?.poll_title || null,
            poll_options: pollPayload?.poll_options || null,
            poll_multiple_choice: pollPayload?.poll_multiple_choice || false,
            poll_secret: pollPayload?.poll_secret || false,
            contact_user_id: contactPayload?.contact_user_id || null,
            contact_name: contactPayload?.contact_name || null,
            contact_avatar_url: contactPayload?.contact_avatar_url || null,
            contact_email: contactPayload?.contact_email || null,
          });
          if (result?.ok && result?.message) {
            await loadMessages(friendId);
            loadUnreadCount();
          }
          // Clear reply after sending
          setReplyToMessage(null);
        } catch (error) {
          console.error('Send message error (HTTP fallback):', error);
        }
        return;
      }

      try {
        // Resolve file URL to full URL for optimistic update
        let baseUrl = '';
        try {
          const apiBaseUrl = await getApiBaseUrlAsync();
          baseUrl = apiBaseUrl.replace('/api', '');
        } catch (error) {
          console.warn('API base URL alınamadı, file URL çözümlenemiyor:', error);
          // Fallback: try synchronous version
          try {
            const apiBaseUrl = getApiBaseUrl();
            if (apiBaseUrl && typeof apiBaseUrl === 'string') {
              baseUrl = apiBaseUrl.replace('/api', '');
            }
          } catch (syncError) {
            console.warn('Sync API base URL de alınamadı:', syncError);
          }
        }
        
        let resolvedFileUrl = fileUrl;
        if (fileUrl && !fileUrl.startsWith('http')) {
          resolvedFileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
        }
        
        const fallbackContent = type === 'note_share' ? i18n.t('chat:chatContext.noteShareContent', { title: notePayload?.note_title || i18n.t('chat:chatContext.noteFallbackLabel') }) : type === 'event_share' ? i18n.t('chat:chatContext.eventShareContent', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollShareContent', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || '');
        const tempMessage = {
          id: `temp_${Date.now()}`,
          senderId: 'current_user',
          content: content ? content.trim() : fallbackContent,
          file_url: resolvedFileUrl,
          file_name: fileName,
          timestamp: new Date().toISOString(),
          type,
          isRead: false,
          note_id: type === 'note_share' ? (notePayload?.note_id || null) : null,
          note_title: type === 'note_share' ? (notePayload?.note_title || null) : null,
          note_content: type === 'note_share' ? (notePayload?.note_content || null) : null,
          note_title_color: type === 'note_share' ? (notePayload?.note_title_color || null) : null,
          event_id: type === 'event_share' ? (eventPayload?.event_id || null) : null,
          event_title: type === 'event_share' ? eventPayload?.event_title : null,
          event_start: type === 'event_share' ? eventPayload?.event_start : null,
          event_end: type === 'event_share' ? eventPayload?.event_end : null,
          event_all_day: type === 'event_share' ? eventPayload?.event_all_day : null,
          event_description: type === 'event_share' ? eventPayload?.event_description : null,
          event_location: type === 'event_share' ? eventPayload?.event_location : null,
          poll_id: type === 'poll' ? null : null,
          poll_title: type === 'poll' ? pollPayload?.poll_title : null,
          poll_multiple_choice: type === 'poll' ? pollPayload?.poll_multiple_choice : null,
          contact_user_id: type === 'contact' ? contactPayload?.contact_user_id : null,
          contact_name: type === 'contact' ? contactPayload?.contact_name : null,
          contact_avatar_url: type === 'contact' ? contactPayload?.contact_avatar_url : null,
          contact_email: type === 'contact' ? contactPayload?.contact_email : null,
        };

        setMessages((prev) => ({
          ...prev,
          [friendId]: [...(prev[friendId] || []), tempMessage],
        }));

        setFriends((prev) =>
          prev.map((friend) =>
            friend.id === friendId || friend.email === friendId
              ? { ...friend, lastMessage: content ? content.trim() : (type === 'note_share' ? i18n.t('chat:chatContext.noteShareContent', { title: notePayload?.note_title || i18n.t('chat:chatContext.noteFallbackLabel') }) : type === 'event_share' ? i18n.t('chat:chatContext.eventSharePreview', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollSharePreview', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || i18n.t('chat:chatContext.fileFallback'))), lastMessageTime: new Date().toISOString() }
              : friend
          )
        );

        socket.emit('message:send', {
          receiver_id: friendId,
          content: content ? content.trim() : fallbackContent,
          type,
          file_url: fileUrl,
          file_name: fileName,
          replyTo: replyTo || replyToMessage?.id || null,
          forwardedFrom: forwardedFrom || null,
          note_id: type === 'note_share' ? (notePayload?.note_id || null) : null,
          note_title: type === 'note_share' ? (notePayload?.note_title || null) : null,
          note_content: type === 'note_share' ? (notePayload?.note_content || null) : null,
          note_title_color: type === 'note_share' ? (notePayload?.note_title_color || null) : null,
          event_id: type === 'event_share' ? (eventPayload?.event_id || null) : null,
          event_title: type === 'event_share' ? eventPayload?.event_title : null,
          event_start: type === 'event_share' ? eventPayload?.event_start : null,
          event_end: type === 'event_share' ? eventPayload?.event_end : null,
          event_all_day: type === 'event_share' ? (eventPayload?.event_all_day === true) : null,
          event_description: type === 'event_share' ? eventPayload?.event_description : null,
          event_location: type === 'event_share' ? eventPayload?.event_location : null,
          poll_title: type === 'poll' ? pollPayload?.poll_title : null,
          poll_options: type === 'poll' ? pollPayload?.poll_options : null,
          poll_multiple_choice: type === 'poll' ? (pollPayload?.poll_multiple_choice === true) : null,
          poll_secret: type === 'poll' ? (pollPayload?.poll_secret === true) : null,
          contact_user_id: type === 'contact' ? contactPayload?.contact_user_id : null,
          contact_name: type === 'contact' ? contactPayload?.contact_name : null,
          contact_avatar_url: type === 'contact' ? contactPayload?.contact_avatar_url : null,
          contact_email: type === 'contact' ? contactPayload?.contact_email : null,
        });

        // Clear reply after sending
        setReplyToMessage(null);

        // Listen for message:sent ACK. Find temp IN LIST so we replace the correct one (fixes 2nd send when multiple pending).
        const handleSent = (data) => {
          const { message } = data;
          if (!message) return;

          let baseUrlResolved = baseUrl;
          setMessages((prev) => {
            const friendMessages = prev[friendId] || [];
            const tempMsg = friendMessages.find((m) => m.id?.startsWith('temp_'));
            if (!tempMsg) {
              const hasReal = friendMessages.some((m) => m.id === message.id);
              if (hasReal) return prev;
              const realMessage = buildRealMessageFromServer(message, baseUrlResolved, null);
              return { ...prev, [friendId]: [...friendMessages.filter((m) => m.id !== message.id), realMessage] };
            }
            let realFileUrl = message.file_url;
            if (realFileUrl && !realFileUrl.startsWith('http') && baseUrlResolved) {
              realFileUrl = `${baseUrlResolved}${realFileUrl.startsWith('/') ? '' : '/'}${realFileUrl}`;
            }
            const realMessage = {
              id: message.id,
              senderId: 'current_user',
              receiverId: message.receiver_id,
              content: message.content,
              type: message.type || 'text',
              file_url: realFileUrl,
              file_name: message.file_name,
              note_id: message.note_id || null,
              note_title: message.note_title || null,
              note_content: message.note_content || null,
              note_title_color: message.note_title_color || null,
              event_id: message.event_id || null,
              event_title: message.event_title || null,
              event_start: message.event_start || null,
              event_end: message.event_end || null,
              event_all_day: message.event_all_day || null,
              event_description: message.event_description || null,
              event_location: message.event_location || null,
              poll_id: message.poll_id || null,
              poll_title: message.poll_title || null,
              poll_multiple_choice: message.poll_multiple_choice || null,
              contact_user_id: tempMsg.contact_user_id ?? message.contact_user_id ?? message.contactUserId ?? null,
              contact_name: tempMsg.contact_name ?? message.contact_name ?? message.contactName ?? null,
              contact_avatar_url: tempMsg.contact_avatar_url ?? message.contact_avatar_url ?? message.contactAvatarUrl ?? null,
              contact_email: tempMsg.contact_email ?? message.contact_email ?? message.contactEmail ?? null,
              timestamp: message.created_at,
              isRead: message.is_read,
              delivered_at: message.delivered_at,
              read_at: message.read_at,
              replyTo: message.replyTo || null,
              isForwarded: message.isForwarded || false,
              forwardedFromMessageId: message.forwardedFromMessageId || null,
              forwardedFromSenderId: message.forwardedFromSenderId || null,
            };
            const filtered = friendMessages.filter((m) => m.id !== tempMsg.id && m.id !== message.id);
            if (filtered.some((m) => m.id === message.id)) return prev;
            return { ...prev, [friendId]: [...filtered, realMessage] };
          });

          socket.off('message:sent', handleSent);
        };

        function buildRealMessageFromServer(message, baseUrlRef, tempMsgFallback) {
          let realFileUrl = message.file_url;
          if (realFileUrl && !realFileUrl.startsWith('http') && baseUrlRef) {
            realFileUrl = `${baseUrlRef}${realFileUrl.startsWith('/') ? '' : '/'}${realFileUrl}`;
          }
          const t = tempMsgFallback || {};
          return {
            id: message.id,
            senderId: 'current_user',
            receiverId: message.receiver_id,
            content: message.content,
            type: message.type || 'text',
            file_url: realFileUrl,
            file_name: message.file_name,
            note_id: message.note_id || null,
            note_title: message.note_title || null,
            note_content: message.note_content || null,
            note_title_color: message.note_title_color || null,
            event_id: message.event_id || null,
            event_title: message.event_title || null,
            event_start: message.event_start || null,
            event_end: message.event_end || null,
            event_all_day: message.event_all_day || null,
            event_description: message.event_description || null,
            event_location: message.event_location || null,
            poll_id: message.poll_id || null,
            poll_title: message.poll_title || null,
            poll_multiple_choice: message.poll_multiple_choice || null,
            contact_user_id: t.contact_user_id ?? message.contact_user_id ?? message.contactUserId ?? null,
            contact_name: t.contact_name ?? message.contact_name ?? message.contactName ?? null,
            contact_avatar_url: t.contact_avatar_url ?? message.contact_avatar_url ?? message.contactAvatarUrl ?? null,
            contact_email: t.contact_email ?? message.contact_email ?? message.contactEmail ?? null,
            timestamp: message.created_at,
            isRead: message.is_read,
            delivered_at: message.delivered_at,
            read_at: message.read_at,
            replyTo: message.replyTo || null,
            isForwarded: message.isForwarded || false,
            forwardedFromMessageId: message.forwardedFromMessageId || null,
            forwardedFromSenderId: message.forwardedFromSenderId || null,
          };
        }

        // Listen for error (remove last temp in list; we don't know which send failed)
        const handleError = (data) => {
          console.error('Message send error:', data);
          setMessages((prev) => {
            const friendMessages = prev[friendId] || [];
            const temps = friendMessages.filter((m) => m.id?.startsWith('temp_'));
            const lastTempId = temps.length ? temps[temps.length - 1].id : null;
            if (!lastTempId) return prev;
            return {
              ...prev,
              [friendId]: friendMessages.filter((m) => m.id !== lastTempId),
            };
          });
          socket.off('message:error', handleError);
        };

        socket.once('message:sent', handleSent);
        socket.once('message:error', handleError);
      } catch (error) {
        console.error('Send message error:', error);
        // Remove temp message on error
        setMessages((prev) => {
          const friendMessages = prev[friendId] || [];
          return {
            ...prev,
            [friendId]: friendMessages.filter((m) => m.id?.startsWith('temp_') ? m.id !== `temp_${Date.now()}` : true),
          };
        });
      }
    },
    [isAuthenticated, user?.id, authToken]
  );


  const deleteMessages = useCallback(async (friendId) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken || !friendId) return false;

    try {
      const result = await chatApi.deleteMessages({
        token: authToken,
        friendId,
      });

      if (result?.ok) {
        // Clear messages from state
        setMessages((prev) => {
          const updated = { ...prev };
          updated[friendId] = [];
          return updated;
        });

        // Update friend's last message to null - this will remove it from sidebar
        // because filteredFriends sorts by lastMessageTime, and friends without lastMessageTime go to the end
        setFriends((prev) =>
          prev.map((friend) =>
            friend.id === friendId
              ? { ...friend, lastMessage: null, lastMessageTime: null }
              : friend
          )
        );

        // If this friend was selected, deselect it
        if (selectedFriendId === friendId) {
          setSelectedFriendId(null);
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete messages error:', error);
      return false;
    }
  }, [user?.id, authToken, selectedFriendId]);

  const pinGroup = useCallback(async (groupId, pinned) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken) {
      console.warn('⚠️ [ChatContext] pinGroup: Not authenticated');
      return false;
    }

    try {
      // Normalize pinned value to boolean
      const pinnedValue = pinned === true || pinned === 'true' || pinned === 1 || pinned === '1';
      
      console.log('📌 [ChatContext] pinGroup: Pinning group', { groupId, pinned: pinnedValue });
      
      const result = await groupApi.pinGroup({
        token: authToken,
        groupId,
        pinned: pinnedValue,
      });

      console.log('📌 [ChatContext] pinGroup: API response', result);

      if (result?.ok) {
        // Use result.pinned if available, otherwise use the pinnedValue we sent
        const finalPinned = result.pinned !== undefined ? (result.pinned === true || result.pinned === 1 || result.pinned === 'true' || result.pinned === '1') : pinnedValue;
        // pinnedAt should be set when pinning, null when unpinning
        const pinnedAt = finalPinned ? (result.pinnedAt || new Date().toISOString()) : null;
        
        console.log('📌 [ChatContext] pinGroup: Updating state', { finalPinned, pinnedAt });
        
        // Update group in state
        setGroups((prev) =>
          prev.map((g) =>
            g.id === groupId
              ? { ...g, pinned: finalPinned, pinnedAt }
              : g
          )
        );

        return true;
      } else {
        console.error('❌ [ChatContext] pinGroup: API returned error', result);
      }

      return false;
    } catch (error) {
      console.error('❌ [ChatContext] pinGroup error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken]);

  const pinFriend = useCallback(async (friendId, pinned) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken) {
      return false;
    }

    try {
      // Normalize pinned value to boolean
      const pinnedValue = pinned === true || pinned === 'true' || pinned === 1 || pinned === '1';
      
      const result = await chatApi.pinFriend({
        token: authToken,
        friendId,
        pinned: pinnedValue,
      });

      if (result?.ok) {
        // Use result.pinned if available, otherwise use the pinnedValue we sent
        const finalPinned = result.pinned !== undefined ? (result.pinned === true || result.pinned === 1 || result.pinned === 'true' || result.pinned === '1') : pinnedValue;
        // pinnedAt should be set when pinning, null when unpinning
        const pinnedAt = finalPinned ? (result.pinnedAt || new Date().toISOString()) : null;
        
        // Track this pin update to prevent race conditions
        recentPinUpdatesRef.current[friendId] = {
          pinned: finalPinned,
          pinnedAt: pinnedAt,
          timestamp: Date.now(),
        };
        
        // Clear the tracking after 10 seconds (enough time for backend to sync)
        setTimeout(() => {
          delete recentPinUpdatesRef.current[friendId];
        }, 10000);
        
        // Update state immediately
        setFriends((prev) =>
          prev.map((friend) => {
            const fId = friend.id || friend.email;
            if (fId === friendId) {
              return { ...friend, pinned: finalPinned, pinnedAt: pinnedAt };
            }
            return friend;
          })
        );
        
        return true;
      } else {
        console.error('Pin friend: API returned error', result);
        return false;
      }
    } catch (error) {
      console.error('Pin friend error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken]);

  const muteFriend = useCallback(async (friendId, mutedUntil) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await chatApi.muteFriend({
        token: authToken,
        friendId,
        mutedUntil,
      });

      if (result?.ok) {
        setFriends((prev) =>
          prev.map((friend) =>
            friend.id === friendId
              ? { ...friend, mutedUntil: result.mutedUntil }
              : friend
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Mute friend error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken]);

  const setNickname = useCallback(async (friendId, nickname) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      // Normalize nickname: trim whitespace, set to null if empty
      const nicknameValue = nickname && typeof nickname === 'string' ? nickname.trim() : null;
      const finalNickname = nicknameValue && nicknameValue.length > 0 ? nicknameValue : null;

      const result = await chatApi.setFriendNickname({
        token: authToken,
        friendId,
        nickname: finalNickname,
      });

      if (result?.ok) {
        setFriends((prev) =>
          prev.map((friend) => {
            const fId = friend.id || friend.email;
            if (fId === friendId) {
              return { ...friend, nickname: result.nickname };
            }
            return friend;
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Set nickname error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken]);

  const deleteFriend = useCallback(async (friendId) => {
    // AuthContext'ten gelen isAuthenticated kontrolü
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await chatApi.deleteFriend({
        token: authToken,
        friendId,
      });

      if (result?.ok) {
        // Mark friend as deleted but keep in list for message history
        setFriends((prev) =>
          prev.map((friend) => {
            const currentFriendId = friend.email || friend.id;
            if (currentFriendId === friendId) {
              // Check if friend has messages and set lastMessageTime from messages
              const friendMessages = messages[friendId] || [];
              let updatedFriend = { ...friend, isDeleted: true };
              
              if (friendMessages.length > 0) {
                // Get last message timestamp
                const lastMessage = friendMessages[friendMessages.length - 1];
                if (lastMessage && lastMessage.timestamp) {
                  updatedFriend.lastMessageTime = lastMessage.timestamp;
                  updatedFriend.lastMessage = lastMessage.content || lastMessage.file_name || '';
                }
              }
              
              return updatedFriend;
            }
            return friend;
          })
        );
        
        // Don't delete messages - keep them for history
        // Messages will remain in state and friend will stay in list with isDeleted flag

        // Don't deselect friend - let user see the messages but can't send new ones
        // The UI will show a warning that friend is deleted

        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete friend error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedFriendId, messages]);


  // Helper function to check if friend is muted
  const isFriendMuted = useCallback((friend) => {
    if (!friend?.mutedUntil) return false;
    const mutedUntil = new Date(friend.mutedUntil);
    const now = new Date();
    return mutedUntil > now;
  }, []);

  // ============================================
  // GROUP CHAT FUNCTIONS
  // ============================================

  // Load groups
  const loadGroups = useCallback(async (skipDebounce = false) => {
    if (!isAuthenticated || !user?.id || !authToken) return;
    
    const executeLoad = async (forceLoad = false) => {
      // If forceLoad is true (skipDebounce), always load (for manual refresh or state sync)
      // Otherwise, skip if socket already provided data
      if (!forceLoad && socketDataReceivedRef.current.groups && socketRef.current?.connected) {
        return;
      }

      try {
        setIsLoading(true);
        const result = await groupApi.getGroups({ token: authToken });
        if (result?.ok && result?.groups) {
          socketDataReceivedRef.current.groups = true;
          setGroups(result.groups);
        }
      } catch (error) {
        console.error('Load groups error:', error);
        socketDataReceivedRef.current.groups = false;
      } finally {
        setIsLoading(false);
      }
    };
    
    if (skipDebounce) {
      await executeLoad(true);
    } else {
      // Use debounce timer for non-critical updates
      loadFriendsTimerRef.current = setTimeout(() => executeLoad(false), 1000);
    }
  }, [user?.id, authToken, isAuthenticated]);

  // Load group messages
  const loadGroupMessages = useCallback(async (groupId, since = null) => {
    if (!isAuthenticated || !user?.id || !authToken || !groupId) return;

    try {
      const result = await groupApi.getGroupMessages({ token: authToken, groupId, since });
      if (result?.ok && result?.messages) {
        let baseUrl = '';
        try {
          const apiBaseUrl = await getApiBaseUrlAsync();
          baseUrl = apiBaseUrl.replace('/api', '');
        } catch (error) {
          console.warn('API base URL alınamadı:', error);
        }
        
        setGroupMessages((prev) => {
          const newMessages = result.messages.map((msg) => {
            let fileUrl = msg.file_url || msg.fileUrl;
            if (fileUrl && !fileUrl.startsWith('http')) {
              fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
            }
            
            return {
              id: msg.id,
              senderId: msg.sender_id,
              senderEmail: msg.sender_email,
              senderName: msg.sender_name || null,
              senderAvatarUrl: msg.sender_avatar_url || null,
              senderIsPlus: msg.sender_isPlus || false,
              content: msg.content,
              type: msg.type || 'text',
              systemEvent: msg.system_event || null,
              systemParams: msg.system_params || null,
              file_url: fileUrl,
              fileUrl,
              file_name: msg.file_name || msg.fileName,
              fileName: msg.file_name || msg.fileName,
              note_id: msg.note_id || null,
              note_title: msg.note_title || null,
              note_content: msg.note_content || null,
              note_title_color: msg.note_title_color || null,
              event_id: msg.event_id || null,
              event_title: msg.event_title || null,
              event_start: msg.event_start || null,
              event_end: msg.event_end || null,
              event_all_day: msg.event_all_day || null,
              event_description: msg.event_description || null,
              event_location: msg.event_location || null,
              poll_id: msg.poll_id || null,
              poll_title: msg.poll_title || null,
              poll_multiple_choice: msg.poll_multiple_choice || null,
              contact_user_id: msg.contact_user_id ?? msg.contactUserId ?? null,
              contact_name: msg.contact_name ?? msg.contactName ?? null,
              contact_avatar_url: msg.contact_avatar_url ?? msg.contactAvatarUrl ?? null,
              contact_email: msg.contact_email ?? msg.contactEmail ?? null,
              timestamp: msg.created_at,
              replyTo: msg.replyTo || null,
              isForwarded: msg.isForwarded || false,
              forwardedFromMessageId: msg.forwardedFromMessageId || null,
              forwardedFromSenderId: msg.forwardedFromSenderId || null,
              mentionedUserIds: msg.mentionedUserIds || [],
              is_deleted_for_everyone: msg.is_deleted_for_everyone || false,
              deleted_by_name: msg.deleted_by_name || null,
            };
          });

          return {
            ...prev,
            [groupId]: newMessages,
          };
        });
      }
    } catch (error) {
      console.error('Load group messages error:', error);
    }
  }, [user?.id, authToken, isAuthenticated]);

  // Reconnect sonrası mesaj boşluğunu doldur: bağlantı kopukken gelen mesajları since parametresiyle çek
  const prevSocketConnectionStateRef = useRef(socketConnectionState);
  useEffect(() => {
    const prev = prevSocketConnectionStateRef.current;
    prevSocketConnectionStateRef.current = socketConnectionState;
    // Sadece DISCONNECTED/RECONNECTING → CONNECTED geçişinde tetikle
    if (
      socketConnectionState === ConnectionState.CONNECTED &&
      prev !== ConnectionState.CONNECTED
    ) {
      if (selectedFriendId) {
        loadMessages(selectedFriendId);
      }
      if (selectedGroupId) {
        const groupCached = groupMessages[selectedGroupId] || [];
        const groupSince = groupCached.length > 0
          ? groupCached[groupCached.length - 1].timestamp
          : null;
        loadGroupMessages(selectedGroupId, groupSince);
      }
    }
  }, [socketConnectionState, selectedFriendId, selectedGroupId, groupMessages, loadMessages, loadGroupMessages]);

  // Create group
  const createGroup = useCallback(async (name, description, avatarUrl, memberEmails) => {
    if (!isAuthenticated || !user?.id || !authToken || !name?.trim()) return null;

    try {
      const result = await groupApi.createGroup({
        token: authToken,
        name: name.trim(),
        description: description?.trim() || null,
        avatarUrl: avatarUrl || null,
        memberEmails: memberEmails || [],
      });

      if (result?.ok && result?.group) {
        // Reload groups (realtime sync via socket)
        loadGroups(true);
        return result.group;
      }
      return null;
    } catch (error) {
      console.error('Create group error:', error);
      
      // Check if it's a group limit error
      if (error?.payload?.error === 'GROUP_LIMIT_REACHED' || error?.message?.includes('limit') || error?.message?.includes('maksimum')) {
        showPlusGoldNotification(i18n.t('shared:plusGoldNotification.features.group'), getApiErrorMessage(error, { fallback: i18n.t('chat:chatContext.groupLimitFallback') }));
      } else {
        // Show generic error toast
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: i18n.t('chat:chatContext.createGroupErrorTitle'),
            message: getApiErrorMessage(error, { fallback: i18n.t('chat:chatContext.createGroupErrorFallback') }),
          },
        }));
      }
      
      // Re-throw error so CreateGroupModal can handle it
      throw error;
    }
  }, [isAuthenticated, user?.id, authToken, loadGroups]);

  // Update group (name, description, avatarUrl)
  const updateGroup = useCallback(async (groupId, updates) => {
    if (!isAuthenticated || !user?.id || !authToken || !groupId) return null;

    try {
      const result = await groupApi.updateGroup({
        token: authToken,
        groupId,
        name: updates.name?.trim() || undefined,
        description: updates.description?.trim() || undefined,
        avatarUrl: updates.avatarUrl || undefined,
      });

      if (result?.ok && result?.group) {
        // Normalize avatar_url from avatarUrl
        const normalizedGroup = {
          ...result.group,
          avatar_url: result.group.avatar_url || result.group.avatarUrl,
        };
        
        // Update local state immediately for smooth UX
        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupId ? { ...group, ...normalizedGroup } : group
          )
        );
        
        // If this is the selected group, update selectedGroup state
        if (selectedGroupId === groupId) {
          setSelectedGroupId(groupId); // Trigger re-render
        }
        
        // Reload groups to ensure sync (realtime sync via socket)
        loadGroups(true);
        
        return normalizedGroup;
      }
      return null;
    } catch (error) {
      console.error('Update group error:', error);
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: {
          type: 'error',
          title: i18n.t('chat:chatContext.updateGroupErrorTitle'),
          message: getApiErrorMessage(error, { fallback: i18n.t('chat:chatContext.updateGroupErrorFallback') }),
        },
      }));
      throw error;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId, loadGroups]);

  // Select group
  const selectGroup = useCallback((groupId) => {
    // Clear friend selection when selecting a group
    setSelectedFriendId(null);
    setSelectedGroupId(groupId);
    if (groupId) {
      loadGroupMessages(groupId);
    }
  }, [loadGroupMessages]);

  // Send group message
  const sendGroupMessage = useCallback(
    async (groupId, content, type = 'text', fileUrl = null, fileName = null, replyTo = null, forwardedFrom = null, mentionedUserIds = [], eventPayload = null, pollPayload = null, contactPayload = null) => {
      if (!isAuthenticated || !user?.id || !authToken) return;
      if (type === 'event_share') {
        if (!eventPayload?.event_title || !eventPayload?.event_start) return;
      } else if (type === 'poll') {
        if (!pollPayload?.poll_title || !pollPayload?.poll_options || !Array.isArray(pollPayload.poll_options) || pollPayload.poll_options.length < 2) return;
      } else if (type === 'contact') {
        if (!contactPayload?.contact_user_id && !contactPayload?.contact_name) return;
      } else if (!content && !fileUrl) return;
      if (type === 'text' && (!content || !content.trim())) return;

      if (!socketManager.isConnected()) {
        await socketManager.forceReconnect();
      }
      const socket = socketManager.getSocket();
      if (!socket || !socket.connected) {
        try {
          const result = await groupApi.sendGroupMessage({
            token: authToken,
            groupId,
            content: content ? content.trim() : (type === 'event_share' ? i18n.t('chat:chatContext.eventShareContent', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollShareContent', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || '')),
            fileUrl,
            fileName,
            type,
            replyTo,
            forwardedFrom,
            mentionedUserIds,
            event_id: eventPayload?.event_id || null,
            event_title: eventPayload?.event_title || null,
            event_start: eventPayload?.event_start || null,
            event_end: eventPayload?.event_end || null,
            event_all_day: eventPayload?.event_all_day || false,
            event_description: eventPayload?.event_description || null,
            event_location: eventPayload?.event_location || null,
            poll_title: pollPayload?.poll_title || null,
            poll_options: pollPayload?.poll_options || null,
            poll_multiple_choice: pollPayload?.poll_multiple_choice || false,
            poll_secret: pollPayload?.poll_secret || false,
            contact_user_id: contactPayload?.contact_user_id || null,
            contact_name: contactPayload?.contact_name || null,
            contact_avatar_url: contactPayload?.contact_avatar_url || null,
          });
          if (result?.ok && result?.message) {
            await loadGroupMessages(groupId);
          }
        } catch (error) {
          console.error('Send group message error (HTTP fallback):', error);
          
          // Check if group is locked (GROUP_PLUS_EXPIRED error)
          if (error?.payload?.error === 'GROUP_PLUS_EXPIRED' || error?.message?.includes('Plus üyeliği sona erdi')) {
            setGroups((prev) =>
              prev.map((group) => {
                if (group.id === groupId) {
                  return {
                    ...group,
                    isLocked: true,
                    lockMessage: getApiErrorMessage(error, { fallback: i18n.t('chat:chatContext.groupLockedFallback') }),
                  };
                }
                return group;
              })
            );
          }
        }
        return;
      }

      try {
        // Resolve file URL
        let baseUrl = '';
        try {
          const apiBaseUrl = await getApiBaseUrlAsync();
          baseUrl = apiBaseUrl.replace('/api', '');
        } catch (error) {
          console.warn('API base URL alınamadı:', error);
        }
        
        let resolvedFileUrl = fileUrl;
        if (fileUrl && !fileUrl.startsWith('http')) {
          resolvedFileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
        }
        
        const fallbackContent = type === 'event_share' ? i18n.t('chat:chatContext.eventShareContent', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollShareContent', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || '');
        const tempMessage = {
          id: `temp_${Date.now()}`,
          senderId: user.id,
          senderEmail: user.email,
          content: content ? content.trim() : fallbackContent,
          file_url: resolvedFileUrl,
          fileUrl: resolvedFileUrl,
          file_name: fileName,
          fileName,
          timestamp: new Date().toISOString(),
          type,
          replyTo: replyTo || null,
          isForwarded: forwardedFrom ? true : false,
          mentionedUserIds: mentionedUserIds || [],
          event_id: type === 'event_share' ? (eventPayload?.event_id || null) : null,
          event_title: type === 'event_share' ? eventPayload?.event_title : null,
          event_start: type === 'event_share' ? eventPayload?.event_start : null,
          event_end: type === 'event_share' ? eventPayload?.event_end : null,
          event_all_day: type === 'event_share' ? eventPayload?.event_all_day : null,
          event_description: type === 'event_share' ? eventPayload?.event_description : null,
          event_location: type === 'event_share' ? eventPayload?.event_location : null,
          poll_id: null,
          poll_title: type === 'poll' ? pollPayload?.poll_title : null,
          poll_multiple_choice: type === 'poll' ? pollPayload?.poll_multiple_choice : null,
          contact_user_id: type === 'contact' ? contactPayload?.contact_user_id : null,
          contact_name: type === 'contact' ? contactPayload?.contact_name : null,
          contact_avatar_url: type === 'contact' ? contactPayload?.contact_avatar_url : null,
          contact_email: type === 'contact' ? contactPayload?.contact_email : null,
        };

        setGroupMessages((prev) => ({
          ...prev,
          [groupId]: [...(prev[groupId] || []), tempMessage],
        }));

        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupId
              ? { ...group, lastMessage: content ? content.trim() : (type === 'event_share' ? i18n.t('chat:chatContext.eventSharePreview', { title: eventPayload?.event_title || i18n.t('chat:chatContext.eventFallbackLabel') }) : type === 'poll' ? i18n.t('chat:chatContext.pollSharePreview', { title: pollPayload?.poll_title || i18n.t('chat:chatContext.pollFallbackLabel') }) : type === 'contact' ? i18n.t('chat:chatContext.contactShareContent', { name: contactPayload?.contact_name || i18n.t('chat:chatContext.contactFallbackLabel') }) : (fileName || i18n.t('chat:chatContext.fileFallback'))), lastMessageTime: new Date().toISOString() }
              : group
          )
        );

        socket.emit('group:message:send', {
          groupId,
          content: content ? content.trim() : fallbackContent,
          type,
          file_url: fileUrl,
          file_name: fileName,
          replyTo,
          forwardedFrom,
          mentionedUserIds,
          event_id: type === 'event_share' ? (eventPayload?.event_id || null) : null,
          event_title: type === 'event_share' ? eventPayload?.event_title : null,
          event_start: type === 'event_share' ? eventPayload?.event_start : null,
          event_end: type === 'event_share' ? eventPayload?.event_end : null,
          event_all_day: type === 'event_share' ? (eventPayload?.event_all_day === true) : null,
          event_description: type === 'event_share' ? eventPayload?.event_description : null,
          event_location: type === 'event_share' ? eventPayload?.event_location : null,
          poll_title: type === 'poll' ? pollPayload?.poll_title : null,
          poll_options: type === 'poll' ? pollPayload?.poll_options : null,
          poll_multiple_choice: type === 'poll' ? (pollPayload?.poll_multiple_choice === true) : null,
          poll_secret: type === 'poll' ? (pollPayload?.poll_secret === true) : null,
          contact_user_id: type === 'contact' ? contactPayload?.contact_user_id : null,
          contact_name: type === 'contact' ? contactPayload?.contact_name : null,
          contact_avatar_url: type === 'contact' ? contactPayload?.contact_avatar_url : null,
          contact_email: type === 'contact' ? contactPayload?.contact_email : null,
        });
      } catch (error) {
        console.error('Send group message error:', error);
      }
    },
    [isAuthenticated, user?.id, authToken, loadGroupMessages]
  );

  /** Send event response (attending | not_attending | maybe). For DM pass groupId=null. Optional calendarId when user has multiple calendars (for attending). */
  const sendEventResponse = useCallback(
    async (messageId, status, groupId = null, calendarId = null) => {
      if (!isAuthenticated || !authToken) return;
      try {
        if (groupId) {
          await groupApi.sendGroupEventResponse({ token: authToken, groupId, messageId, status, calendar_id: calendarId });
        } else {
          await chatApi.sendEventResponse({ token: authToken, messageId, status, calendar_id: calendarId });
        }
        setLastEventResponse({ messageId, groupId });
      } catch (error) {
        console.error('Send event response error:', error);
        throw error;
      }
    },
    [isAuthenticated, authToken]
  );

  /** Get event participants for a message. For DM pass groupId=null. */
  const getEventParticipants = useCallback(
    async (messageId, groupId = null) => {
      if (!authToken) return { participants: [] };
      try {
        if (groupId) {
          const res = await groupApi.getGroupEventParticipants({ token: authToken, groupId, messageId });
          return res?.ok ? { participants: res.participants || [] } : { participants: [] };
        }
        const res = await chatApi.getEventParticipants({ token: authToken, messageId });
        return res?.ok ? { participants: res.participants || [] } : { participants: [] };
      } catch (error) {
        if (error?.status !== 404 && error?.payload?.error !== 'MESSAGE_NOT_FOUND') {
          console.error('Get event participants error:', error);
        }
        return { participants: [] };
      }
    },
    [authToken]
  );

  /** Get poll with options and vote counts */
  const getPoll = useCallback(
    async (pollId) => {
      if (!authToken || !pollId) return null;
      try {
        const res = await chatApi.getPoll({ token: authToken, pollId });
        return res?.ok ? { poll: res.poll, options: res.options || [] } : null;
      } catch (error) {
        console.error('Get poll error:', error);
        return null;
      }
    },
    [authToken]
  );

  /** Vote on poll (option_id or option_ids for multiple_choice) */
  const votePoll = useCallback(
    async (pollId, optionIdOrIds) => {
      if (!authToken || !pollId) return;
      const option_ids = Array.isArray(optionIdOrIds) ? optionIdOrIds : [optionIdOrIds];
      const option_id = option_ids.length === 1 ? option_ids[0] : undefined;
      try {
        await chatApi.votePoll({ token: authToken, pollId, option_id, option_ids: option_ids.length > 1 ? option_ids : undefined });
        setLastPollVoted((prev) => ({ ...prev, pollId, messageId: null, groupId: null, groupMessageId: null }));
      } catch (error) {
        console.error('Vote poll error:', error);
      }
    },
    [authToken]
  );

  const getPollVotesDetail = useCallback(
    async (pollId) => {
      if (!authToken || !pollId) return null;
      try {
        const res = await chatApi.getPollVotesDetail({ token: authToken, pollId });
        return res?.ok ? { poll: res.poll, options: res.options || [] } : null;
      } catch (error) {
        console.error('Get poll votes detail error:', error);
        return null;
      }
    },
    [authToken]
  );

  // Mute/unmute group
  const muteGroup = useCallback(async (groupId, muted) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.muteGroup({
        token: authToken,
        groupId,
        muted,
      });

      if (result?.ok) {
        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupId
              ? { ...group, muted: result.muted }
              : group
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Mute group error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken]);

  // Leave group
  const leaveGroup = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.leaveGroup({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Remove group from state (realtime sync via socket)
        loadGroups(true);
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Leave group error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId, loadGroups]);

  // Delete group
  const deleteGroup = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.deleteGroup({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Remove group from state (realtime sync via socket)
        loadGroups(true);
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete group error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId, loadGroups]);

  // Destroy group (owner only)
  const destroyGroup = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.destroyGroup({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Reload groups to reflect destroyed state
        loadGroups(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Destroy group error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, loadGroups]);

  // Clear group messages (personal)
  const clearGroupMessages = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.clearGroupMessages({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Reload messages to reflect cleared state
        if (selectedGroupId === groupId) {
          loadGroupMessages(groupId);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Clear group messages error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId, loadGroupMessages]);

  // Delete group messages (hard delete, owner only)
  const deleteGroupMessages = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.deleteGroupMessages({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Remove group from sidebar (realtime sync via socket)
        loadGroups(true);
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete group messages error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId, loadGroups]);

  // Remove group from sidebar (persistent - hidden in DB)
  const removeGroupFromSidebar = useCallback(async (groupId) => {
    if (!isAuthenticated || !user?.id || !authToken) return false;

    try {
      const result = await groupApi.hideGroup({
        token: authToken,
        groupId,
      });

      if (result?.ok) {
        // Remove group from state (persistent - won't reappear until new message)
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        
        // Deselect if currently selected
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
        
        // Clear messages for this group from state
        setGroupMessages((prev) => {
          const updated = { ...prev };
          delete updated[groupId];
          return updated;
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Hide group error:', error);
      return false;
    }
  }, [isAuthenticated, user?.id, authToken, selectedGroupId]);

  // Group notification handler (with @mention check)
  const handleGroupNotificationRef = useRef(null);
  const handleGroupNotification = useCallback(async (groupId, message) => {
    // System messages should NOT trigger notifications
    if (message.type === 'system') {
      return false;
    }
    
    const group = groups.find((g) => g.id === groupId);
    if (!group) return false;

    // Check if group is muted
    if (group.muted) {
      // Check if user is mentioned
      const mentionedUserIds = message.mentionedUserIds || [];
      const isMentioned = mentionedUserIds.includes(user?.id);
      
      if (!isMentioned) {
        // Group is muted and user is not mentioned - no notification
        return false;
      }
      // User is mentioned - always notify (even if muted)
    }

    // Check if user is on chat page and this group is selected
    const locationRefPath = locationRef.current;
    const windowLocationPath = typeof window !== 'undefined' && window.location ? 
      (window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname) : 
      null;
    const currentPath = windowLocationPath || locationRefPath;
    const isUserOnChatPage = currentPath === '/chat' || currentPath.startsWith('/chat');
    const isGroupSelected = selectedGroupIdRef.current === groupId;
    
    if (isUserOnChatPage && isGroupSelected) {
      // User is viewing this group - no notification
      return false;
    }

    // Show notification
    const isMuted = preferences?.groupNotificationsMuted === true;
    if (!isMuted && preferences) {
      try {
        const selectedSound = preferences.groupNotificationSoundId || preferences.chatNotificationSoundId || 'auramessage';
        await playChatSound(selectedSound);
      } catch (error) {
        console.warn('Group notification sound failed:', error);
      }
    }

    // Show toast notification (if not on chat page); skip when app lock active
    const isFocused = typeof document !== 'undefined' && !document.hidden && document.hasFocus();
    if (!isUserOnChatPage && isFocused && !getLockActive(user?.id)) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-chat-toast', {
          detail: {
            group,
            messageId: message.id,
            isGroup: true,
          },
        }));
      }
    }

    // Update badge
    // (Badge update is handled by unreadCount state)

    return true;
  }, [groups, user?.id, preferences]);

  // Update ref when handleGroupNotification changes
  useEffect(() => {
    handleGroupNotificationRef.current = handleGroupNotification;
  }, [handleGroupNotification]);

  // Refs for group state
  const selectedGroupIdRef = useRef(selectedGroupId);
  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  // Initial load groups
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !authToken) {
      return;
    }

    loadGroups(true);
  }, [isAuthenticated, user?.id, authToken, loadGroups]);

  // Load group messages when group is selected
  useEffect(() => {
    if (selectedGroupId) {
      loadGroupMessages(selectedGroupId);
    }
  }, [selectedGroupId, loadGroupMessages]);

  // Computed values
  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  const selectedGroupMessages = useMemo(
    () => (selectedGroupId ? groupMessages[selectedGroupId] || [] : []),
    [groupMessages, selectedGroupId]
  );

  const value = {
    socketConnectionState,
    socketManager,
    // Direct chat
    friends,
    messages,
    selectedFriend,
    selectedFriendId,
    selectedFriendMessages,
    filteredFriends,
    searchQuery,
    setSearchQuery,
    selectFriend,
    sendMessage,
    pinFriend,
    muteFriend,
    setNickname,
    isFriendMuted,
    deleteMessages,
    deleteFriend,
    unreadCount,
    hasUnreadMessages,
    hasUnreadChatInBackground,
    isLoading,
    loadingMessagesForFriendId,
    loadUnreadCount,
    pendingFriendRequestCount,
    loadPendingFriendRequestCount,
    reloadFriends: loadFriends,
    reloadMessages: (friendId) => loadMessages(friendId, { forceFullLoad: true }),
    removeDmMessage: (friendId, messageId) => setMessages((prev) => {
      const msgs = prev[friendId];
      if (!msgs) return prev;
      return { ...prev, [friendId]: msgs.filter((m) => m.id !== messageId) };
    }),
    updateDmMessage: (friendId, messageId, patch) => setMessages((prev) => {
      const msgs = prev[friendId];
      if (!msgs) return prev;
      return { ...prev, [friendId]: msgs.map((m) => m.id === messageId ? { ...m, ...patch } : m) };
    }),
    removeGroupMessage: (groupId, messageId) => setGroupMessages((prev) => {
      const msgs = prev[groupId];
      if (!msgs) return prev;
      return { ...prev, [groupId]: msgs.filter((m) => m.id !== messageId) };
    }),
    updateGroupMessage: (groupId, messageId, patch) => setGroupMessages((prev) => {
      const msgs = prev[groupId];
      if (!msgs) return prev;
      return { ...prev, [groupId]: msgs.map((m) => m.id === messageId ? { ...m, ...patch } : m) };
    }),
    pendingAdminNotification,
    clearAdminNotification: () => setPendingAdminNotification(null),
    replyToMessage,
    setReplyToMessage,
    forwardMessage,
    setForwardMessage,
    lastEventResponse,
    sendEventResponse,
    getEventParticipants,
    lastPollVoted,
    getPoll,
    votePoll,
    getPollVotesDetail,
    // Group chat
    groups,
    groupMessages,
    selectedGroup,
    selectedGroupId,
    selectedGroupMessages,
    groupTyping,
    groupMessageReads,
    selectGroup,
    sendGroupMessage,
    createGroup,
    updateGroup,
    muteGroup,
    pinGroup,
    leaveGroup,
    deleteGroup,
    destroyGroup,
    clearGroupMessages,
    deleteGroupMessages,
    removeGroupFromSidebar,
    loadGroups,
    loadGroupMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
